/**
 * Owned storefront core runtime.
 *
 * This readable source owns global UI, navigation, media, and cart-drawer behavior.
 * Product-only custom elements live in main-product.js; main.js is retained only as
 * an unloaded compatibility reference while the split settles.
 */
const SESSION_COUNTDOWN_STORAGE_PREFIX="exa:session-countdown:v1",
sessionCountdownEngine=(()=>{
  const registrations=new Map;
  let ticker=null,
  nextRegistrationId=0;
  function readStoredNumber(key)
  {
    try{
      const value=Number(window.sessionStorage.getItem(key));
      return Number.isSafeInteger(value)&&value>0?value:null
    }
    catch{
      return null
    }
  }
  function writeStoredNumber(key, value)
  {
    try{
      window.sessionStorage.setItem(key, String(value))
    }
    catch{
    }
  }
  const sessionStartedAtKey=SESSION_COUNTDOWN_STORAGE_PREFIX+":started-at";
  let sessionStartedAt=readStoredNumber(sessionStartedAtKey);
  if(sessionStartedAt===null)
  {
    sessionStartedAt=Date.now(),
    writeStoredNumber(sessionStartedAtKey, sessionStartedAt)
  }
  function getDeadline(key, durationSeconds)
  {
    const storageKey=SESSION_COUNTDOWN_STORAGE_PREFIX+":deadline:"+encodeURIComponent(key);
    let deadline=readStoredNumber(storageKey);
    if(deadline===null)
    {
      const duration=Number(durationSeconds);
      const maximumDurationSeconds=Math.max(0, Math.floor((Number.MAX_SAFE_INTEGER-sessionStartedAt)/1000)),
      requestedDurationSeconds=Number.isFinite(duration)?duration>0?Math.max(1, Math.floor(duration)):0:duration>0?maximumDurationSeconds:0,
      safeDurationSeconds=Math.min(Number.isSafeInteger(requestedDurationSeconds)?requestedDurationSeconds:maximumDurationSeconds, maximumDurationSeconds);
      deadline=sessionStartedAt+safeDurationSeconds*1000,
      writeStoredNumber(storageKey, deadline)
    }
    return deadline
  }
  function stopTickerIfIdle()
  {
    if(registrations.size===0&&ticker!==null)
    {
      clearInterval(ticker),
      ticker=null
    }
  }
  function updateRegistration(registration, now)
  {
    const remainingSeconds=Math.max(0, Math.ceil((registration.deadline-now)/1000));
    if(remainingSeconds!==registration.remainingSeconds)
    {
      registration.remainingSeconds=remainingSeconds,
      registration.onTick(remainingSeconds, registration.deadline)
    }
    if(now>=registration.deadline)
    {
      registrations.delete(registration.id),
      registration.onExpire(registration.deadline)
    }
  }
  function tick()
  {
    const now=Date.now();
    Array.from(registrations.values()).forEach(registration=>updateRegistration(registration, now)),
    stopTickerIfIdle()
  }
  return{
    register(key, durationSeconds, onTick, onExpire)
    {
      const registration={
        id:++nextRegistrationId,
        deadline:getDeadline(key, durationSeconds),
        remainingSeconds:null,
        onTick:typeof onTick==="function"?onTick:()=>{},
        onExpire:typeof onExpire==="function"?onExpire:()=>{}
      };
      registrations.set(registration.id, registration),
      updateRegistration(registration, Date.now());
      if(registrations.size>0&&ticker===null)ticker=setInterval(tick, 1000);
      return registrations.has(registration.id)?registration.id:null
    },
    unregister(registrationId)
    {
      registrations.delete(registrationId),
      stopTickerIfIdle()
    },
    restart(key, durationSeconds)
    {
      const now=Date.now(),
      storageKey=SESSION_COUNTDOWN_STORAGE_PREFIX+":deadline:"+encodeURIComponent(key),
      currentDeadline=readStoredNumber(storageKey);
      if(currentDeadline!==null&&currentDeadline>now)return currentDeadline;
      const duration=Number(durationSeconds),
      maximumDurationSeconds=Math.max(0, Math.floor((Number.MAX_SAFE_INTEGER-now)/1000)),
      requestedDurationSeconds=Number.isFinite(duration)?duration>0?Math.max(1, Math.floor(duration)):0:duration>0?maximumDurationSeconds:0,
      safeDurationSeconds=Math.min(Number.isSafeInteger(requestedDurationSeconds)?requestedDurationSeconds:maximumDurationSeconds, maximumDurationSeconds),
      deadline=now+safeDurationSeconds*1000;
      writeStoredNumber(storageKey, deadline);
      return deadline
    }
  }
})();
window.sessionCountdownEngine=sessionCountdownEngine;
class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    const duration=Number(this.dataset.duration),
    maximumSafeDurationSeconds=Math.max(0, Math.floor((Number.MAX_SAFE_INTEGER-Date.now())/1000)),
    requestedDurationSeconds=Number.isFinite(duration)?duration>0?Math.max(1, Math.floor(duration)):0:duration>0?maximumSafeDurationSeconds:0;
    this.duration=Math.min(Number.isSafeInteger(requestedDurationSeconds)?requestedDurationSeconds:maximumSafeDurationSeconds, maximumSafeDurationSeconds),
    this.countdownKey=this.dataset.countdownKey||"cart:"+this.duration,
    this.registrationId=null,
    this.isPlaying=!1,
    this.initTimer(),
    this.updateTimer(this.duration)
  }
  connectedCallback() { this.playTimer() }
  disconnectedCallback() { this.pauseTimer() }
  initTimer() {
    this.innerHTML="",
    this.minutesSpan=document.createElement("span");
    const separator=document.createTextNode(":");
    this.secondsSpan=document.createElement("span"),
    this.append(this.minutesSpan, separator, this.secondsSpan)
  }
  updateTimer(remainingSeconds=this.duration) {
    const remaining=Math.max(0, Math.floor(remainingSeconds));
    this.minutesSpan.textContent=this.formatNumber(Math.floor(remaining/60)),
    this.secondsSpan.textContent=this.formatNumber(remaining%60)
  }
  playTimer() {
    if(this.isPlaying)return;
    this.isPlaying=!0,
    this.registrationId=null;
    const engine=window.sessionCountdownEngine;
    if(!engine||typeof engine.register!=="function") {
      this.isPlaying=!1;
      return
    }
    const registrationId=engine.register(this.countdownKey, this.duration, remaining=>this.updateTimer(remaining), ()=>this.expire());
    if(this.isPlaying&&registrationId!==null)this.registrationId=registrationId;
    else if(registrationId!==null)engine.unregister(registrationId)
  }
  pauseTimer() {
    const engine=window.sessionCountdownEngine;
    this.registrationId!==null&&engine&&typeof engine.unregister==="function"&&engine.unregister(this.registrationId),
    this.registrationId=null,
    this.isPlaying=!1
  }
  expire() {
    this.updateTimer(0),
    this.pauseTimer();
    const timerBlock=this.closest(".cart-timer")||this;
    timerBlock.style.display="none"
  }
  formatNumber(t) { return 1===t.toString().length?"0"+t:t }
}
customElements.define("countdown-timer", CountdownTimer);
class DateCountdownTimer extends HTMLElement {
  connectedCallback() {
    if(this.update=this.update.bind(this), this.wrapper=this.closest(".countdown-wrapper"), !this.wrapper)return;
    this.mode=this.wrapper.dataset.countdownMode||"fixed_date",
    this.behavior=this.wrapper.dataset.endBehavior,
    this.rollingEndBehavior=this.wrapper.dataset.rollingEndBehavior||"hide",
    this.repeatHours=parseInt(this.wrapper.dataset.repeatHours)||24,
    this.labels={days:this.wrapper.dataset.labelDays,hours:this.wrapper.dataset.labelHours,minutes:this.wrapper.dataset.labelMinutes,seconds:this.wrapper.dataset.labelSeconds},
    this.units={days:"true"===this.wrapper.dataset.showDays,hours:"true"===this.wrapper.dataset.showHours,minutes:"true"===this.wrapper.dataset.showMinutes,seconds:"true"===this.wrapper.dataset.showSeconds},
    this.timerStyle=this.dataset.style;
    if(this.mode==="rolling") {
      this.startRolling();
      return
    }
    const endTime=(this.wrapper.dataset.endTime||"").trim(),
    timezone=this.wrapper.dataset.timezone,
    utcOffset=parseInt(this.wrapper.dataset.utcOffset)||0;
    if(this.endTime=this.parseEndTime(endTime, timezone, utcOffset), !(this.endTime instanceof Date)||isNaN(this.endTime.getTime())) {
      this.hideCountdown();
      return
    }
    this.timer=setInterval(this.update, 1e3),
    this.update()
  }
  startRolling() {
    const configuredMinutes=Number(this.wrapper.dataset.rollingDurationMinutes),
    durationMinutes=Number.isFinite(configuredMinutes)&&configuredMinutes>0?configuredMinutes:10,
    maximumSafeDurationSeconds=Math.max(1, Math.floor((Number.MAX_SAFE_INTEGER-Date.now())/1000)),
    requestedDurationSeconds=durationMinutes*60,
    durationSeconds=Math.min(Number.isFinite(requestedDurationSeconds)?Math.max(1, Math.ceil(requestedDurationSeconds)):maximumSafeDurationSeconds, maximumSafeDurationSeconds),
    countdownKey=this.wrapper.dataset.countdownKey||"date:"+durationSeconds,
    engine=window.sessionCountdownEngine;
    this.rollingDurationSeconds=durationSeconds,
    this.rollingCountdownKey=countdownKey,
    this.rollingActive=!0,
    this.rollingRegistrationId=null;
    if(!engine||typeof engine.register!=="function") {
      this.rollingActive=!1;
      return
    }
    const registrationId=engine.register(countdownKey, durationSeconds, remaining=>this.renderTime(remaining), ()=>this.expireRolling());
    if(this.rollingActive&&registrationId!==null)this.rollingRegistrationId=registrationId;
    else if(registrationId!==null)engine.unregister(registrationId)
  }
  stopRolling() {
    const engine=window.sessionCountdownEngine;
    this.rollingRegistrationId!==null&&engine&&typeof engine.unregister==="function"&&engine.unregister(this.rollingRegistrationId),
    this.rollingRegistrationId=null,
    this.rollingActive=!1
  }
  expireRolling() {
    this.renderTime(0),
    this.stopRolling();
    const engine=window.sessionCountdownEngine;
    if(this.rollingEndBehavior==="restart"&&engine&&typeof engine.restart==="function") {
      engine.restart(this.rollingCountdownKey, this.rollingDurationSeconds),
      this.startRolling();
      return
    }
    this.hideCountdown(),
    this.hideOwnedAnnouncementSlide()
  }
  hideCountdown() {
    this.wrapper.style.display="none"
  }
  hideOwnedAnnouncementSlide() {
    if(this.wrapper.dataset.countdownAnnouncementSlide!=="true")return;
    const announcementItem=this.wrapper.parentElement,
    slideContainer=announcementItem&&announcementItem.parentElement,
    slide=slideContainer&&slideContainer.parentElement;
    if(announcementItem&&announcementItem.classList.contains("announcement-bar__item")&&announcementItem.children.length===1&&announcementItem.firstElementChild===this.wrapper&&slideContainer&&slideContainer.classList.contains("splide__slide__container")&&slideContainer.children.length===1&&slideContainer.firstElementChild===announcementItem&&slide&&slide.classList.contains("splide__slide")&&slide.children.length===1&&slide.firstElementChild===slideContainer)slide.style.display="none"
  }
  parseEndTime(t, e, i) { let s=t.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/); if(!s) { let n=new Date(t.replace(/-/g, "/")); return n } let r=parseInt(s[1], 10),o=parseInt(s[2], 10)-1,a=parseInt(s[3], 10),l=parseInt(s[4], 10),d=parseInt(s[5], 10),c=parseInt(s[6]||"0", 10); if("fixed"!==e)return new Date(r, o, a, l, d, c); { let h=Date.UTC(r, o, a, l, d, c)-36e5*i; return new Date(h) } }
  update() { let t=new Date,e=Math.floor((this.endTime-t)/1e3); if(!isFinite(e))return; if(e<=0) { if("hide"===this.behavior) { this.hideCountdown(),clearInterval(this.timer); return } if("restart"===this.behavior) { let i=new Date(this.endTime),s=36e5*this.repeatHours; for(;i<=t;)i=new Date(i.getTime()+s); this.endTime=i,e=Math.floor((this.endTime-t)/1e3) } else if("next_day"===this.behavior) { let n=new Date(this.endTime); for(;n<=t;)n.setDate(n.getDate()+1); this.endTime=n,e=Math.floor((this.endTime-t)/1e3) } } this.renderTime(e) }
  renderTime(remainingSeconds) { let r=Math.floor(remainingSeconds/86400),o=Math.floor(remainingSeconds%86400/3600),a=Math.floor(remainingSeconds%3600/60),l=remainingSeconds%60,d={days:r,hours:o,minutes:a,seconds:l}; Object.keys(this.units).forEach(t=>{if(!this.units[t])return;let e=this.querySelector(`[aria-label="${"days"===t?"Day":"hours"===t?"Hour":"minutes"===t?"Minute":"Second"}"]`);if(!e)return;let i=e.querySelector(".countdown-timer__number");if(!i)return;let s=String(d[t]).padStart(2,"0");"4"===this.timerStyle?(i.innerHTML="",s.split("").forEach(t=>{let e=document.createElement("span");e.className="countdown-timer__digit",e.textContent=t,i.appendChild(e)})):i.textContent=s,i.setAttribute("aria-label",`${d[t]} ${this.labels[t]}`)}) }
  disconnectedCallback() { clearInterval(this.timer),this.timer=null; if(this.mode==="rolling")this.stopRolling() }
}
customElements.define("date-countdown-timer", DateCountdownTimer);
const ON_CHANGE_DEBOUNCE_TIMER=300,
currentDate=new Date,
PUB_SUB_EVENTS={
  cartUpdate:"cart-update",
  quantityUpdate:"quantity-update",
  optionValueSelectionChange:"option-value-selection-change",
  variantChange:"variant-change",
  cartError:"cart-error"
};
let subscribers={
};
function subscribe(id_0001, id_0002)
{
  return subscribers[id_0001]===void 0&&(subscribers[id_0001]=[]),
  subscribers[id_0001]=[...subscribers[id_0001],
  id_0002],
  function()
  {
    subscribers[id_0001]=subscribers[id_0001]["filter"](id_0003=>id_0003!==id_0002)
  }
}
function publish(id_0004, id_0005)
{
  subscribers[id_0004]&&subscribers[id_0004].forEach(id_0006=>{
    id_0006(id_0005)
  })
}
function parseSectionElement(sectionHTML, selector)
{
  const element=new DOMParser().parseFromString(sectionHTML, "text/html").querySelector(selector);
  if(!element)throw new Error("Cart section is missing "+selector);
  return element
}
class CartUpdateCoordinator
{
  constructor()
  {
    this.abortController=null,
    this.refreshPromise=null
  }
  abortActiveRequest()
  {
    this.abortController&&this.abortController.abort()
  }
  isAbortError(error)
  {
    return error&&error.name==="AbortError"
  }
  async request(url, options, parser, operation)
  {
    this.abortActiveRequest();
    const controller=new AbortController;
    this.abortController=controller;
    try{
      const response=await fetch(url, {
        ...options,
        signal:controller.signal
      }),
      result=await parser(response);
      if(!response.ok)
      {
        const message=result&&typeof result==="object"&&(result.description||result.message);
        throw new Error(message||operation+" failed ("+response.status+")")
      }
      return result
    }
    finally{
      this.abortController===controller&&(this.abortController=null)
    }
  }
  fetchJSON(url, options={}, operation="Cart update")
  {
    return this.request(url, options, async response=>{
      const responseText=await response.text();
      if(!responseText)return{};
      try{
        return JSON.parse(responseText)
      }
      catch{
        if(!response.ok)return{};
        throw new Error("Invalid "+operation.toLowerCase()+" response ("+response.status+")")
      }
    }, operation)
  }
  fetchText(url, options={}, operation="Cart refresh")
  {
    return this.request(url, options, response=>response.text(), operation)
  }
  refreshMainCartItems()
  {
    if(this.refreshPromise)return this.refreshPromise;
    this.refreshPromise=this.fetchText("/cart?section_id=main-cart-items", {}, "Cart refresh").then(sectionHTML=>{
      const freshCartItems=parseSectionElement(sectionHTML, "cart-items");
      document.querySelectorAll("cart-items").forEach(cartItems=>{
        cartItems.innerHTML=freshCartItems.innerHTML
      })
    }).catch(error=>{
      this.isAbortError(error)||console.error(error)
    }).finally(()=>{
      this.refreshPromise=null
    });
    return this.refreshPromise
  }
}
const cartUpdateCoordinator=new CartUpdateCoordinator;
function getVisibleCartItemCount(id_0007=document)
{
  let id_0008=0;
  return id_0007&&id_0007.querySelectorAll&&id_0007.querySelectorAll(".cart-item").forEach(id_0009=>{
    if(id_0009.classList.contains("cart-item--hidden"))return;
    id_0008+=Number(id_0009.dataset.quantity||1)||1
  }),
  id_0008
}
function syncCartDrawerVisibleEmptyState(id_0010=document.querySelector("cart-drawer"))
{
  if(!id_0010)return 0;
  const id_0011=getVisibleCartItemCount(id_0010),
  id_0012=id_0011<=0,
  id_0013=id_0010.querySelector("cart-drawer-items"),
  id_0014=document.getElementById("main-cart-footer");
  id_0010.classList.toggle("is-empty", id_0012),
  id_0013&&id_0013.classList.toggle("is-empty", id_0012),
  id_0014&&id_0014.classList.toggle("is-empty", id_0012);
  return id_0011
}
function hiddenUpsellDebugEnabled()
{
  try{
    return localStorage.getItem("hiddenUpsellDebug")==="true"
  }
  catch{
    return !1
  }
}
function hiddenUpsellDebugLog(id_0014a, id_0014b={})
{
  if(!hiddenUpsellDebugEnabled())return;
  const id_0014c={
    step:id_0014a, timestamp:new Date().toISOString(), ...id_0014b
  };
  console.log("[hidden-upsell]", id_0014c);
  try{
    const id_0014d=JSON.parse(localStorage.getItem("hiddenUpsellDebugLog")||"[]");
    id_0014d.push(id_0014c),
    localStorage.setItem("hiddenUpsellDebugLog", JSON.stringify(id_0014d.slice(-50)))
  }
  catch{}
}
async function getHiddenUpsellCartSnapshot()
{
  const id_0014e=buildAjaxCartUrl(routes.cart_url, "/cart"),
  id_0014f=await fetch(id_0014e, {
    headers:{
      Accept:"application/json"
    }
  });
  if(!id_0014f.ok)throw new Error("Cart snapshot failed ("+id_0014f.status+")");
  return id_0014f.json()
}
function hiddenUpsellDelay(id_0014g)
{
  return new Promise(id_0014h=>setTimeout(id_0014h, id_0014g))
}
function getMissingHiddenCartUpsellItems(id_0014i, id_0014j)
{
  const id_0014k=new Set;
  id_0014j&&id_0014j.items&&id_0014j.items.forEach(id_0014l=>{
    const id_0014m=String(id_0014l.variant_id||id_0014l.id||"");
    id_0014m&&id_0014k.add(id_0014m)
  });
  return id_0014i.filter(id_0014n=>!id_0014k.has(String(id_0014n.id)))
}
async function confirmHiddenCartUpsells(id_0014o)
{
  let id_0014p=null,
  id_0014q=id_0014o;
  for(let id_0014r=0; id_0014r<5; id_0014r++)
  {
    id_0014p=await getHiddenUpsellCartSnapshot(),
    id_0014q=getMissingHiddenCartUpsellItems(id_0014o, id_0014p),
    hiddenUpsellDebugLog("confirm-cart", {
      attempt:id_0014r+1,
      missingItems:id_0014q,
      itemCount:id_0014p.item_count
    });
    if(!id_0014q.length)return{
      ok:!0, cart:id_0014p, missingItems:[]
    };
    await hiddenUpsellDelay(150*(id_0014r+1))
  }
  return{
    ok:!1, cart:id_0014p, missingItems:id_0014q
  }
}
function getHiddenCartUpsellItems(id_0018a=document)
{
  const id_0015=new Set;
  document.querySelectorAll(".cart-item[data-variant-id]").forEach(id_0016=>{
    id_0016.dataset.variantId&&id_0015.add(String(id_0016.dataset.variantId))
  });
  id_0018a!==document&&id_0018a.querySelectorAll&&id_0018a.querySelectorAll(".cart-item[data-variant-id]").forEach(id_0016=>{
    id_0016.dataset.variantId&&id_0015.add(String(id_0016.dataset.variantId))
  });
  const id_0017=[];
  id_0018a&&id_0018a.querySelectorAll&&id_0018a.querySelectorAll("[data-hidden-upsell-item][data-variant-id]").forEach(id_0018=>{
    const id_0019=String(id_0018.dataset.variantId||"");
    if(!id_0019||id_0015.has(id_0019))return;
    id_0015.add(id_0019);
    const id_0020={
      id:id_0019,
      quantity:1,
      properties:{
        _cart_hidden:"true"
      }
    };
    id_0018.dataset.sellingPlanId&&(id_0020.selling_plan=id_0018.dataset.sellingPlanId),
    id_0017.push(id_0020)
  });
  hiddenUpsellDebugLog("metadata-read", {
    source:id_0018a===document?"document":"fresh-section",
    existingVariantIds:[...id_0015],
    candidateItems:id_0017
  });
  return id_0017
}
async function getFreshHiddenCartUpsellItems()
{
  const id_0018a=routes.cart_url||"/cart",
  id_0018b=id_0018a+(id_0018a.includes("?")?"&":"?")+"sections=cart-drawer&sections_url="+encodeURIComponent(window.location.pathname),
  id_0018c=await fetch(id_0018b, {
    headers:{
      Accept:"application/json"
    }
  });
  if(!id_0018c.ok)throw new Error("Hidden upsell refresh failed ("+id_0018c.status+")");
  const id_0018d=await id_0018c.json(),
  id_0018e=id_0018d["cart-drawer"];
  if(!id_0018e)return[];
  const id_0018f=new DOMParser().parseFromString(id_0018e, "text/html");
  hiddenUpsellDebugLog("fresh-section-read", {
    hasCartDrawerSection:!!id_0018e,
    hiddenMetadataCount:id_0018f.querySelectorAll("[data-hidden-upsell-item][data-variant-id]").length
  });
  return getHiddenCartUpsellItems(id_0018f)
}
async function addHiddenCartUpsells(id_0021)
{
  if(!id_0021.length)return;
  const id_0022=buildAjaxCartUrl(routes.cart_add_url, "/cart/add"),
  id_0021a={
    ...fetchConfig(), credentials:"same-origin", body:JSON.stringify({
      items:id_0021
    })
  },
  id_0023=await fetch(id_0022, id_0021a);
  hiddenUpsellDebugLog("batch-add-response", {
    status:id_0023.status,
    ok:id_0023.ok,
    items:id_0021
  });
  if(id_0023.ok)return;
  let id_0024={},
  id_0024a="";
  try{
    id_0024a=await id_0023.text(),
    id_0024=id_0024a?JSON.parse(id_0024a):{}
  }
  catch{}
  hiddenUpsellDebugLog("batch-add-error-body", {
    status:id_0023.status,
    body:id_0024a,
    parsed:id_0024
  });
  const id_0025a=new Error(id_0024.description||id_0024.message||"Hidden upsell add failed ("+id_0023.status+")");
  let id_0025b=0;
  for(const id_0025c of id_0021)
  {
    try{
      const id_0025d=await fetch(id_0022, {
        ...fetchConfig(), credentials:"same-origin", body:JSON.stringify({
          items:[id_0025c]
        })
      });
      hiddenUpsellDebugLog("single-add-response", {
        status:id_0025d.status,
        ok:id_0025d.ok,
        item:id_0025c
      });
      if(id_0025d.ok)id_0025b+=1;
      else{
        let id_0025f="";
        try{id_0025f=await id_0025d.text()}catch{}
        hiddenUpsellDebugLog("single-add-error-body", {
          status:id_0025d.status,
          item:id_0025c,
          body:id_0025f
        })
      }
    }
    catch(id_0025e){
      console.error(id_0025e)
    }
  }
  if(id_0025b===0)throw id_0025a
}
window.handleHiddenCartUpsellsCheckout=function(id_0025, id_0026)
{
  id_0025&&id_0025.preventDefault&&id_0025.preventDefault(),
  id_0026&&(id_0026.disabled=!0),
  (async()=>{
    hiddenUpsellDebugLog("checkout-click", {
      path:window.location.pathname,
      domMetadataCount:document.querySelectorAll("[data-hidden-upsell-item][data-variant-id]").length
    });
    let id_0027=getHiddenCartUpsellItems();
    if(!id_0027.length)id_0027=await getFreshHiddenCartUpsellItems();
    hiddenUpsellDebugLog("items-before-add", {
      items:id_0027
    });
    if(id_0027.length)
    {
      await addHiddenCartUpsells(id_0027);
      let id_0027a=await confirmHiddenCartUpsells(id_0027);
      if(!id_0027a.ok&&id_0027a.missingItems.length)
      {
        hiddenUpsellDebugLog("retry-missing-items", {
          items:id_0027a.missingItems
        }),
        await addHiddenCartUpsells(id_0027a.missingItems),
        id_0027a=await confirmHiddenCartUpsells(id_0027)
      }
      hiddenUpsellDebugLog("cart-after-add", {
        confirmed:id_0027a.ok,
        itemCount:id_0027a.cart&&id_0027a.cart.item_count,
        items:id_0027a.cart&&id_0027a.cart.items&&id_0027a.cart.items.map(id_0027b=>({
          id:id_0027b.id,
          variant_id:id_0027b.variant_id,
          product_id:id_0027b.product_id,
          handle:id_0027b.handle,
          quantity:id_0027b.quantity,
          properties:id_0027b.properties
        }))
      }),
      await hiddenUpsellDelay(150)
    }
  })().catch(id_0028=>{
    console.error(id_0028),
    hiddenUpsellDebugLog("error", {
      message:id_0028&&id_0028.message?id_0028.message:String(id_0028)
    })
  }).finally(()=>{
    if(hiddenUpsellDebugEnabled()&&localStorage.getItem("hiddenUpsellDebugPause")==="true")
    {
      id_0026&&(id_0026.disabled=!1, id_0026.classList.remove("loading"));
      const id_0028a=id_0026&&id_0026.querySelector(".loading-overlay__spinner");
      id_0028a&&id_0028a.classList.add("hidden");
      hiddenUpsellDebugLog("redirect-paused");
      return
    }
    window.location="/checkout"
  });
  return !0
};
window.checkoutLoad=function(id_0028b)
{
  const id_0028c=id_0028b&&id_0028b.currentTarget,
  id_0028d=id_0028c&&id_0028c.closest("cart-drawer"),
  id_0028e=id_0028d&&id_0028d.querySelector("tnc-chekcbox[id^=\"TNC-Checkbox-\"]")||document.querySelector("tnc-chekcbox[id^=\"TNC-Checkbox-\"]"),
  id_0028ea=id_0028e&&id_0028e.querySelector('input[type="checkbox"]');
  if(id_0028e&&(id_0028ea?!id_0028ea.checked:id_0028e.dataset.checked!="true"))return;
  if(!id_0028c)return;
  id_0028c.classList.add("loading");
  const id_0028f=id_0028c.querySelector(".loading-overlay__spinner");
  id_0028f&&id_0028f.classList.remove("hidden");
  if(window.handleHiddenCartUpsellsCheckout&&window.handleHiddenCartUpsellsCheckout(id_0028b, id_0028c))return;
  setTimeout(()=>{
    if(document.location.pathname.includes("/checkout"))return;
    id_0028c.classList.remove("loading"),
    id_0028c.disabled=!1,
    id_0028f&&id_0028f.classList.add("hidden")
  }, 8e3)
};
window.hiddenUpsellDebug={
  enable:function(id_0029=!0)
  {
    localStorage.setItem("hiddenUpsellDebug", "true"),
    localStorage.setItem("hiddenUpsellDebugPause", id_0029?"true":"false"),
    localStorage.removeItem("hiddenUpsellDebugLog");
    return "Hidden upsell debug enabled. Click checkout, then run hiddenUpsellDebug.log()."
  },
  disable:function()
  {
    localStorage.removeItem("hiddenUpsellDebug"),
    localStorage.removeItem("hiddenUpsellDebugPause");
    return "Hidden upsell debug disabled."
  },
  log:function()
  {
    try{
      return JSON.parse(localStorage.getItem("hiddenUpsellDebugLog")||"[]")
    }
    catch{
      return[]
    }
  },
  inspect:async function()
  {
    const id_0030=getHiddenCartUpsellItems(),
    id_0031=await getFreshHiddenCartUpsellItems().catch(id_0032=>({
      error:id_0032.message
    })),
    id_0033=await getHiddenUpsellCartSnapshot().catch(id_0034=>({
      error:id_0034.message
    }));
    return{
      domItems:id_0030,
      freshItems:id_0031,
      cart:id_0033
    }
  }
};
const GIFTS_LOCAL_STORAGE_KEY="exa_gift_goals";
let shouldCheckExaGifts=!1,
giftsToMonitor=[];
function initExaGiftsCheck(id_0007=!1)
{
  let id_0008=document.querySelector("cart-drawer-items")||document["querySelector"]("cart-items");
  if(id_0007&&(id_0008=document["querySelector"]("cart-items")), !id_0008||id_0008.dataset["giftsStorage"]!=="true")return;
  const id_0009=localStorage["getItem"](GIFTS_LOCAL_STORAGE_KEY);
  if(!id_0009)return;
  const id_0010=JSON["parse"](id_0009);
  !id_0010||typeof id_0010!=="object"||(Object["values"](id_0010)["forEach"](id_0011=>{
    !id_0008["querySelector"](".cart-item--product-"+id_0011["gift_handle"])||document["querySelector"](".gift--product-"+id_0011["gift_handle"])||(shouldCheckExaGifts=!0, giftsToMonitor["push"](id_0011))
  }), updateGiftStyles(), checkExaGifts(id_0007))
}
function updateGiftStyles()
{
  let id_0012=document["getElementById"]("exa-gifts-style");
  if(id_0012&&id_0012["remove"](), !giftsToMonitor["length"])return;
  const id_0013=document["createElement"]("style");
  id_0013.id="exa-gifts-style";
  const id_0014=giftsToMonitor["map"](id_0015=>".cart-item--product-"+id_0015["gift_handle"]+" .quantity { display: none !important; }").join(`
`);
  id_0013["textContent"]=id_0014,
  document.head["appendChild"](id_0013)
}
function checkExaGifts(id_0016=!1)
{
  let id_0017=document["querySelector"]("cart-drawer-items")||document["querySelector"]("cart-items");
  if(id_0016&&(id_0017=document["querySelector"]("cart-items")), !(!shouldCheckExaGifts||!id_0017||!id_0017["updateQuantity"]))for(const id_0018 of giftsToMonitor)
  {
    const id_0019=id_0017["querySelector"](".cart-item--product-"+id_0018.gift_handle);
    if(!id_0019)
    {
      const id_0020=giftsToMonitor["findIndex"](id_0021=>id_0021["gift_handle"]===id_0018["gift_handle"]);
      id_0020>-1&&giftsToMonitor.splice(id_0020, 1),
      removeLocalStorageGift(id_0018);
      continue
    }
    let id_0022=!1;
    if(id_0018["type"]==="product_quantity")
    {
      let id_0023=0;
      id_0017["querySelectorAll"](".cart-item--product-"+id_0018["unlock_product_handle"]).forEach(id_0024=>{
        id_0023+=parseInt(id_0024["dataset"]["quantity"])
      }),
      id_0023<parseInt(id_0018["required_quantity"])&&(id_0022=!0)
    }
    else if(id_0018["type"]==="variant_option")
    {
      const id_0023a=Math.max(0, (parseInt(id_0018.option_position)||1)-1),
      id_0023b=String(id_0018.required_quantity||"").split(",").map(id_0023c=>id_0023c.trim().toLowerCase()).filter(Boolean);
      let id_0023d=!1;
      id_0017["querySelectorAll"](".cart-item--product-"+id_0018["unlock_product_handle"]).forEach(id_0023e=>{
        try
        {
          const id_0023f=JSON.parse(id_0023e.dataset.variantOptions||"[]"), id_0023g=String(id_0023f[id_0023a]||"").trim().toLowerCase();
          id_0023b.includes(id_0023g)&&(id_0023d=!0)
        }
        catch(id_0023h){}
      }),
      id_0023d||(id_0022=!0)
    }
    else{
      let id_0022a=parseInt(id_0017["dataset"]["subtotal"])||0;
      id_0019&&(id_0022a-=parseInt(id_0019["dataset"]["linePrice"])||0),
      id_0022a<parseInt(id_0018["required_quantity"])&&(id_0022=!0)
    }
    if(id_0022)
    {
      const id_0025=giftsToMonitor.findIndex(id_0026=>id_0026["gift_handle"]===id_0018.gift_handle);
      id_0025>-1&&giftsToMonitor["splice"](id_0025, 1);
      removeLocalStorageGift(id_0018);
      const cartItemIndex=id_0019["dataset"]["index"]??id_0019.id["replace"]("CartItem-", "");
      id_0017["updateQuantity"](cartItemIndex, 0);
      return
    }
  }
}
function removeLocalStorageGift(id_0027)
{
  const id_0028=localStorage.getItem(GIFTS_LOCAL_STORAGE_KEY);
  if(id_0028)try{
    const id_0029=JSON["parse"](id_0028);
    id_0029&&id_0029[id_0027.gift_handle]&&(delete id_0029[id_0027["gift_handle"]], localStorage["setItem"](GIFTS_LOCAL_STORAGE_KEY, JSON["stringify"](id_0029)))
  }
  catch(id_0030)
  {
    console["warn"]("Error parsing gift localStorage data:", id_0030)
  }
}
class CartRemoveButton extends HTMLElement
{
  constructor()
  {
    super(),
    this["isLoading"]=!1,
    this["loadingResetTimer"]=null,
    this["addEventListener"]("click", this["handleRemoveClick"].bind(this))
  }
  disconnectedCallback()
  {
    this.loadingResetTimer&&clearTimeout(this["loadingResetTimer"])
  }
  ["handleRemoveClick"](id_0031)
  {
    if(id_0031["preventDefault"](), this["isLoading"])return;
    this["setLoadingState"](!0);
    const id_0032=Date.now();
    if(this["dataset"]["handle"])
    {
      const id_0033=this["dataset"]["handle"]; document["querySelectorAll"]('.quantity-break__upsell[data-handle="'+id_0033+'"]')["forEach"](id_0034=>{
        id_0034["dataset"].selected="false", id_0034["classList"]["remove"]("quantity-break__upsell--selected"); const id_0035=id_0034["querySelector"]('input[type="checkbox"]'); id_0035&&(id_0035["checked"]=!1)
      })
    }
    const id_0036=this["closest"]("cart-items")||this["closest"]("cart-drawer-items");
    if(!id_0036)
    {
      this["setLoadingState"](!1);
      return
    }
    let id_0037;
    try{
      id_0037=this["clearCart"]?id_0036.clearCart():id_0036.updateQuantity(this["dataset"]["index"], 0)
    }
    catch{
      this["setLoadingState"](!1);
      return
    }
    this.loadingResetTimer&&clearTimeout(this["loadingResetTimer"]),
    this["loadingResetTimer"]=setTimeout(()=>{
      this["setLoadingState"](!1)
    }, 12e3),
    Promise.resolve(id_0037)["catch"](()=>{})["finally"](()=>{
      const id_0038=Math.max(0, 350-(Date.now()-id_0032)); setTimeout(()=>{
        this["setLoadingState"](!1)
      }, id_0038)
    })
  }
  ["setLoadingState"](id_0036)
  {
    this["isLoading"]=id_0036;
    const id_0039=this["querySelector"]("button, a");
    if(!id_0039)return;
    const id_0040=id_0039["querySelector"]("svg");
    id_0036?(id_0039.setAttribute("aria-disabled", "true"), id_0039["style"]["pointerEvents"]="none", id_0039["style"]["opacity"]="0.6", id_0040&&id_0040["style"]&&(id_0040.style["display"]="none"), this["loadingSpinner"]||(this["loadingSpinner"]=document["createElement"]("span"), this["loadingSpinner"]["setAttribute"]("aria-hidden", "true"), this["loadingSpinner"]["style"]["display"]="inline-block", this["loadingSpinner"].style["width"]="1.45rem", this["loadingSpinner"]["style"]["height"]="1.45rem", this["loadingSpinner"]["style"]["border"]="0.2rem solid currentColor", this["loadingSpinner"]["style"]["borderLeftColor"]="transparent", this["loadingSpinner"]["style"]["borderRadius"]="50%", this["loadingSpinner"]["style"]["verticalAlign"]="middle", this["loadingSpinner"]["style"]["boxSizing"]="border-box"), id_0039["contains"](this["loadingSpinner"])||id_0039["appendChild"](this["loadingSpinner"]), this.loadingSpinnerAnimation&&this["loadingSpinnerAnimation"]["cancel"](), this["loadingSpinnerAnimation"]=this.loadingSpinner["animate"]([
      {
        transform:"rotate(0deg)"
      },
      {
        transform:"rotate(360deg)"
      }], {
      duration:700,
      iterations:Infinity
    })):(this["loadingResetTimer"]&&clearTimeout(this["loadingResetTimer"]), this["loadingResetTimer"]=null, id_0039["removeAttribute"]("aria-disabled"), id_0039["style"]["pointerEvents"]="", id_0039.style["opacity"]="", id_0040&&id_0040["style"]&&(id_0040.style["display"]=""), this.loadingSpinnerAnimation&&(this["loadingSpinnerAnimation"]["cancel"](), this["loadingSpinnerAnimation"]=null), this["loadingSpinner"]&&this.loadingSpinner["remove"]())
  }
}
customElements["define"]("cart-remove-button", CartRemoveButton);
class CartItems extends HTMLElement
{
  constructor()
  {
    super(),
    this.lineItemContainer=!0,
    this["lineItemStatusElement"]=document.getElementById("shopping-cart-line-item-status")||document["getElementById"]("CartDrawer-LineItemStatus"),
    this["secondCartItems"]=document.querySelector("cart-drawer-items"),
    this["quantityGifts"]=document.querySelector(".quantity-gifts");
    const id_0036=debounce(id_0037=>{
      this["onChange"](id_0037)
    }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener("change", id_0036["bind"](this)),
    this["isCartPage"]=this["nodeName"]==="CART-ITEMS",
    this["otherCartItemsSelector"]=this["isCartPage"]?"cart-drawer-items":"cart-items",
    this.hasOtherCartItems=document.querySelector(this["otherCartItemsSelector"])!==null,
    this.hasOtherCartItems?this.isCartPage&&initExaGiftsCheck(!0):initExaGiftsCheck(!1)
  }
  cartUpdateUnsubscriber=void 0;
  ["connectedCallback"]()
  {
    this.cartUpdateUnsubscriber=subscribe(PUB_SUB_EVENTS["cartUpdate"], id_0038=>{
      id_0038["source"]!=="cart-items"&&this.isCartPage&&this["onCartUpdate"]()
    })
  }
  ["disconnectedCallback"]()
  {
    this["cartUpdateUnsubscriber"]&&this["cartUpdateUnsubscriber"]()
  }
  ["onChange"](id_0039)
  {
    this.updateQuantity(id_0039.target["dataset"].index, id_0039["target"].value, document["activeElement"]["getAttribute"]("name"))
  }
  ["onCartUpdate"]()
  {
    return cartUpdateCoordinator.refreshMainCartItems()
  }
  getCartOwners()
  {
    return[this, this.secondCartItems].filter((owner, index, owners)=>owner&&owners.indexOf(owner)===index)
  }
  getCoordinatedSectionsToRender()
  {
    const sections=new Set;
    this.getCartOwners().forEach(owner=>owner.getSectionsToRender().forEach(section=>{
      sections.add(section.section||section.id)
    }));
    return[...sections]
  }
  renderSectionsFromCartData(cartData)
  {
    if(!cartData||!cartData.sections)return;
    this.classList.toggle("is-empty", cartData.item_count===0),
    this.getSectionsToRender().forEach(section=>{
      const sectionHTML=cartData.sections[section.section||section.id],
      container=document.getElementById(section.id);
      if(!sectionHTML||!container)return;
      const content=container.querySelector(section.selector)||container;
      content.innerHTML=this.getSectionInnerHTML(sectionHTML, section.selector);
      const cartBody=content.querySelector(".cart-drawer__body");
      cartBody&&this.cartDrawer&&(cartBody.scrollTop=this.cartDrawer.cartBodyScroll)
    })
  }
  async updateCart()
  {
    try{
      const cartData=await cartUpdateCoordinator.fetchJSON("/cart/update.js", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          updates:{},
          sections:this.getCoordinatedSectionsToRender(),
          sections_url:window.location.pathname
        })
      }, "Cart refresh");
      this.getCartOwners().forEach(owner=>owner.renderSectionsFromCartData(cartData));
      return cartData
    }
    catch(error)
    {
      if(!cartUpdateCoordinator.isAbortError(error))console.error(error)
    }
  }
  ["getSectionsToRender"]()
  {
    return[{
      id:"main-cart-items",
      section:document["getElementById"]("main-cart-items")["dataset"].id,
      selector:".js-contents"
    },
    {
      id:"cart-icon-bubble",
      section:"cart-icon-bubble",
      selector:".shopify-section"
    },
    {
      id:"cart-live-region-text",
      section:"cart-live-region-text",
      selector:".shopify-section"
    },
    {
      id:"main-cart-footer",
      section:document["getElementById"]("main-cart-footer").dataset.id,
      selector:".js-contents"
    }]
  }
  async["updateQuantity"](id_0052, id_0053, id_0054)
  {
    const quantityUpdateToken=Symbol("quantity-update");
    this.quantityUpdateToken=quantityUpdateToken;
    let dispatchCompletion=!1;
    this["enableLoading"](id_0052);
    const id_0055=document.getElementById("cart-errors")||document["getElementById"]("CartDrawer-CartErrors");
    id_0055&&(id_0055["textContent"]="");
    const id_0056=JSON.stringify({
      line:id_0052, quantity:id_0053, sections:this.getCoordinatedSectionsToRender(), sections_url:window["location"]["pathname"]
    });
    try{
      const id_0058=buildAjaxCartUrl(routes["cart_change_url"], "/cart/change"),
      id_0060=await cartUpdateCoordinator.fetchJSON(id_0058, {
        ...fetchConfig(), body:id_0056
      }, "Cart change");
      dispatchCompletion=!0;
      const id_0061=document.getElementById("Quantity-"+id_0052)||document["getElementById"]("Drawer-quantity-"+id_0052),
      id_0062=document["querySelectorAll"](".cart-item");
      if(id_0060["errors"])
      {
        id_0061["value"]=id_0061.getAttribute("value"),
        this["updateLiveRegions"](id_0052, id_0060["errors"]);
        return
      }
      if(!this["lineItemContainer"])return;
      if(!id_0060["sections"])
      {
        id_0060.sections=await cartUpdateCoordinator.fetchJSON(routes.cart_url+"?sections="+this.getCoordinatedSectionsToRender()["join"](","), {}, "Cart section refresh")
      }
      this.classList["toggle"]("is-empty", id_0060["item_count"]===0);
      const id_0065=document["querySelector"]("cart-drawer"),
      id_0066=document["getElementById"]("main-cart-footer");
      id_0066&&id_0066["classList"]["toggle"]("is-empty", id_0060.item_count===0),
      id_0065&&id_0065["classList"]["toggle"]("is-empty", id_0060["item_count"]===0),
      this.getCartOwners().forEach(owner=>owner.renderSectionsFromCartData(id_0060));
      const id_0070a=syncCartDrawerVisibleEmptyState(id_0065);
      const id_0071=id_0060["items"][id_0052-1]?id_0060.items[id_0052-1]["quantity"]:void 0;
      let id_0072="";
      id_0062.length===id_0060.items["length"]&&id_0071!==parseInt(id_0061["value"])&&(typeof id_0071==="undefined"?id_0072=window["cartStrings"]["error"]:id_0072=window["cartStrings"]["quantityError"]["replace"]("[quantity]", id_0071)),
      this["updateLiveRegions"](id_0052, id_0072);
      const id_0073=document.getElementById("CartItem-"+id_0052)||document.getElementById("CartDrawer-Item-"+id_0052);
      if(id_0073&&id_0073["querySelector"]("[name=\""+id_0054+'"]')?id_0065?trapFocus(id_0065, id_0073["querySelector"]("[name=\""+id_0054+'"]')):id_0073["querySelector"]("[name=\""+id_0054+'"]')["focus"]():id_0070a===0&&id_0065?trapFocus(id_0065["querySelector"](".drawer__inner-empty"), id_0065["querySelector"]("a")):document["querySelector"](".cart-item:not(.cart-item--hidden)")&&id_0065&&trapFocus(id_0065, document["querySelector"](".cart-item:not(.cart-item--hidden) .cart-item__name")), id_0065)
      {
        id_0065["checkForClear"]();
        const id_0074=id_0065["querySelector"]("countdown-timer");
        id_0074&&id_0074["playTimer"]();
        let id_0075=!1;
        if(id_0065["querySelector"]("cart-drawer-gift, cart-drawer-upsell[data-toggle=\"true\"]"))
        {
          id_0065["checkForClear"]();
          let id_0076=[],
          id_0077=[];
          id_0065["querySelectorAll"]('cart-drawer-gift, cart-drawer-upsell[data-toggle="true"]')["forEach"](id_0078=>{
            id_0078["getUpdateRequired"]()&&(id_0065["querySelector"](".cart-item--product-"+id_0078["dataset"]["handle"])?id_0078["dataset"]["selected"]==="false"&&id_0077.push(id_0078):id_0078["dataset"]["selected"]==="true"&&id_0076["push"](id_0078))
          }),
          id_0077["length"]>0?(id_0075=!0, id_0077[0]["removeFromCart"]()):id_0076.length>0&&(id_0075=!0, id_0076[0].addToCart())
        }
        this["quantityGifts"]&&!id_0075&&this["quantityGifts"].initUnlock(),
        document["querySelectorAll"]("quantity-break-upsells")["forEach"](id_0079=>{
          id_0075||id_0079["initUnlock"]()
        })
      }
      publish(PUB_SUB_EVENTS["cartUpdate"], {
        source:"cart-items", cartData:id_0060
      })
    }
    catch(id_0080)
    {
      if(cartUpdateCoordinator.isAbortError(id_0080))return;
      console["error"](id_0080),
      this["querySelectorAll"](".loading-overlay").forEach(id_0081=>id_0081["classList"]["add"]("hidden"));
      const id_0082=document["getElementById"]("cart-errors")||document["getElementById"]("CartDrawer-CartErrors");
      id_0082["textContent"]=id_0080&&id_0080["message"]?id_0080["message"]:window["cartStrings"]["error"]
    }
    finally{
      if(this.quantityUpdateToken!==quantityUpdateToken)return;
      this.disableLoading(id_0052);
      if(!dispatchCompletion)return;
      const id_0083=new CustomEvent("cartQuantityUpdated", {
        detail:{
          handle:"handle"
        }
      });
      document["dispatchEvent"](id_0083),
      shouldCheckExaGifts&&checkExaGifts(this["isCartPage"])
    }
  }
  ["updateLiveRegions"](id_0084, id_0085)
  {
    const id_0086=document["getElementById"]("Line-item-error-"+id_0084)||document["getElementById"]("CartDrawer-LineItemError-"+id_0084);
    id_0086&&(id_0086.querySelector(".cart-item__error-text")["innerHTML"]=id_0085),
    this.lineItemStatusElement.setAttribute("aria-hidden", !0);
    const id_0087=document["getElementById"]("cart-live-region-text")||document["getElementById"]("CartDrawer-LiveRegionText");
    id_0087["setAttribute"]("aria-hidden", !1),
    setTimeout(()=>{
      id_0087["setAttribute"]("aria-hidden", !0)
    }, 1e3)
  }
  ["getSectionInnerHTML"](id_0088, id_0089)
  {
    return parseSectionElement(id_0088, id_0089).innerHTML
  }
  enableLoading(id_0090)
  {
    (document.getElementById("main-cart-items")||document.getElementById("CartDrawer-CartItems"))["classList"]["add"]("cart__items--disabled");
    const id_0091=this["querySelectorAll"]("#CartItem-"+id_0090+" .loading-overlay"),
    id_0092=this["querySelectorAll"]("#CartDrawer-Item-"+id_0090+" .loading-overlay");
    [...id_0091,
    ...id_0092]["forEach"](id_0093=>id_0093["classList"]["remove"]("hidden")),
    document.activeElement["blur"](),
    this.lineItemStatusElement["setAttribute"]("aria-hidden", !1)
  }
  ["disableLoading"](id_0094)
  {
    (document["getElementById"]("main-cart-items")||document.getElementById("CartDrawer-CartItems")).classList["remove"]("cart__items--disabled");
    const id_0095=this["querySelectorAll"]("#CartItem-"+id_0094+" .loading-overlay"),
    id_0096=this["querySelectorAll"]("#CartDrawer-Item-"+id_0094+" .loading-overlay");
    id_0095["forEach"](id_0097=>id_0097.classList.add("hidden")),
    id_0096["forEach"](id_0098=>id_0098.classList["add"]("hidden"))
  }
  ["clearCart"]()
  {
    const id_0099=document.getElementById("cart-errors")||document["getElementById"]("CartDrawer-CartErrors");
    id_0099&&(id_0099["textContent"]="");
    const id_0100=JSON["stringify"]({
      sections:this.getCoordinatedSectionsToRender(), sections_url:window.location["pathname"]
    }),
    id_0101=buildAjaxCartUrl(routes.cart_clear_url, "/cart/clear");
    return cartUpdateCoordinator.fetchJSON(id_0101, {
      ...fetchConfig(), body:id_0100
    }, "Clear cart")["then"](async id_0104=>{
      if(!id_0104["sections"])try{
        const id_0105=this.getCoordinatedSectionsToRender().join(","), id_0108=await cartUpdateCoordinator.fetchJSON(routes["cart_url"]+"?sections="+id_0105, {}, "Clear cart section refresh"); id_0104["sections"]=id_0108
      }
      catch(id_0109)
      {
        throw console["error"]("Clear cart fallback failed", id_0109), id_0109
      }
      this["classList"]["add"]("is-empty"); const id_0110=document["querySelector"]("cart-drawer"), id_0111=document["getElementById"]("main-cart-footer"); id_0111&&id_0111["classList"]["add"]("is-empty"), id_0110&&id_0110["classList"]["add"]("is-empty"), this.getCartOwners().forEach(owner=>owner.renderSectionsFromCartData(id_0104)), id_0110&&trapFocus(id_0110["querySelector"](".drawer__inner-empty"), id_0110["querySelector"]("a")), publish(PUB_SUB_EVENTS.cartUpdate, {
        source:"cart-items"
      }), shouldCheckExaGifts&&checkExaGifts(this["isCartPage"])
    })["catch"](id_0114=>{
      if(cartUpdateCoordinator.isAbortError(id_0114))return;
      console["error"](id_0114),
      this["querySelectorAll"](".loading-overlay").forEach(id_0115=>id_0115["classList"]["add"]("hidden")); const id_0116=document.getElementById("cart-errors")||document["getElementById"]("CartDrawer-CartErrors"); id_0116["textContent"]=id_0114&&id_0114["message"]?id_0114["message"]:window["cartStrings"]["error"]
    })
  }
}
customElements.define("cart-items", CartItems);
var search="search";
!customElements["get"]("cart-note")&&customElements.define("cart-note", class extends HTMLElement
{
  constructor()
  {
    super(), this["addEventListener"]("change", debounce(id_0116=>{
      const id_0117=JSON["stringify"]({
        note:id_0116["target"]["value"]
      }); fetch(""+routes.cart_update_url, {
        ...fetchConfig(), body:id_0117
      })
    }, ON_CHANGE_DEBOUNCE_TIMER))
  }
});
function handleDiscountForm(id_0118)
{
  id_0118["preventDefault"]();
  const id_0119=id_0118["target"]["querySelector"]("[name=cart-discount-field]"),
  id_0120=id_0118["target"]["querySelector"](".cart-discount-form__error"),
  id_0121=id_0119["value"];
  if(id_0121===void 0||id_0121["length"]===0)
  {
    id_0120["style"]["display"]="block";
    return
  }
  id_0120["style"]["display"]="none";
  const id_0122="/checkout?discount=",
  id_0123=id_0122+id_0121;
  window.location.href=id_0123
}
function handleDiscountFormChange(id_0124)
{
  document["querySelectorAll"](".cart-discount-form__error")["forEach"](id_0125=>{
    id_0125["style"]["display"]="none"
  })
}
class SearchForm extends HTMLElement
{
  constructor()
  {
    super(),
    this["input"]=this.querySelector("input[type=\"search\"]"),
    this["resetButton"]=this["querySelector"]('button[type="reset"]'),
    this.input&&(this.input["form"]["addEventListener"]("reset", this["onFormReset"]["bind"](this)), this["input"].addEventListener("input", debounce(id_0126=>{
      this["onChange"](id_0126)
    }, 300).bind(this)))
  }
  toggleResetButton()
  {
    const id_0127=this["resetButton"]["classList"]["contains"]("hidden");
    this["input"]["value"]["length"]>0&&id_0127?this["resetButton"]["classList"].remove("hidden"):this["input"].value["length"]===0&&!id_0127&&this["resetButton"]["classList"]["add"]("hidden")
  }
  ["onChange"]()
  {
    this["toggleResetButton"]()
  }
  ["shouldResetForm"]()
  {
    return!document["querySelector"]("[aria-selected=\"true\"] a")
  }
  ["onFormReset"](id_0128)
  {
    id_0128["preventDefault"](),
    this.shouldResetForm()&&(this.input["value"]="", this.input["focus"](), this["toggleResetButton"]())
  }
}
customElements["define"]("search-form", SearchForm);
class PredictiveSearch extends SearchForm
{
  constructor()
  {
    super(),
    this["cachedResults"]={
    },
    this["predictiveSearchResults"]=this.querySelector("[data-predictive-search]"),
    this["allPredictiveSearchInstances"]=document.querySelectorAll("predictive-search"),
    this.isOpen=!1,
    this["abortController"]=new AbortController,
    this["searchTerm"]="",
    this.setupEventListeners()
  }
  ["setupEventListeners"]()
  {
    this["input"].form["addEventListener"]("submit", this["onFormSubmit"]["bind"](this)),
    this["input"]["addEventListener"]("focus", this["onFocus"].bind(this)),
    this["addEventListener"]("focusout", this["onFocusOut"].bind(this)),
    this["addEventListener"]("keyup", this.onKeyup["bind"](this)),
    this["addEventListener"]("keydown", this.onKeydown.bind(this))
  }
  ["getQuery"]()
  {
    return this["input"].value["trim"]()
  }
  ["onChange"]()
  {
    super["onChange"]();
    const id_0129=this["getQuery"]();
    if((!this["searchTerm"]||!id_0129["startsWith"](this.searchTerm))&&this.querySelector("#predictive-search-results-groups-wrapper")?.remove(), this["updateSearchForTerm"](this["searchTerm"], id_0129), this["searchTerm"]=id_0129, !this["searchTerm"]["length"])
    {
      this["close"](!0);
      return
    }
    this["getSearchResults"](this.searchTerm)
  }
  ["onFormSubmit"](id_0130)
  {
    (!this["getQuery"]().length||this.querySelector("[aria-selected=\"true\"] a"))&&id_0130.preventDefault()
  }
  ["onFormReset"](id_0131)
  {
    super.onFormReset(id_0131),
    super["shouldResetForm"]()&&(this.searchTerm="", this["abortController"].abort(), this["abortController"]=new AbortController, this["closeResults"](!0))
  }
  ["onFocus"]()
  {
    const id_0132=this["getQuery"]();
    id_0132.length&&(this["searchTerm"]!==id_0132?this["onChange"]():this["getAttribute"]("results")==="true"?this.open():this.getSearchResults(this.searchTerm))
  }
  ["onFocusOut"]()
  {
    setTimeout(()=>{
      this["contains"](document["activeElement"])||this["close"]()
    })
  }
  ["onKeyup"](id_0133)
  {
    switch(this["getQuery"]()["length"]||this["close"](!0), id_0133.preventDefault(), id_0133["code"])
    {
      case "ArrowUp":this["switchOption"]("up");
      break;
      case "ArrowDown":this.switchOption("down");
      break;
      case"Enter":this["selectOption"]();
      break
    }
  }
  ["onKeydown"](id_0134)
  {
    (id_0134["code"]==="ArrowUp"||id_0134["code"]==="ArrowDown")&&id_0134.preventDefault()
  }
  ["updateSearchForTerm"](id_0135, id_0136)
  {
    const id_0137=this["querySelector"]("[data-predictive-search-search-for-text]"),
    id_0138=id_0137?.["innerText"];
    if(id_0138)
    {
      if(id_0138["match"](new RegExp(id_0135, "g")).length>1)return;
      const id_0139=id_0138["replace"](id_0135, id_0136);
      id_0137.innerText=id_0139
    }
  }
  switchOption(id_0140)
  {
    if(!this["getAttribute"]("open"))return;
    const id_0141=id_0140==="up",
    id_0142=this["querySelector"]("[aria-selected=\"true\"]"),
    id_0143=Array.from(this.querySelectorAll("li, button.predictive-search__item"))["filter"](id_0144=>id_0144["offsetParent"]!==null);
    let id_0145=0;
    if(id_0141&&!id_0142)return;
    let id_0146=-1,
    id_0147=0;
    for(; id_0146===-1&&id_0147<=id_0143.length; )id_0143[id_0147]===id_0142&&(id_0146=id_0147),
    id_0147++;
    if(this["statusElement"]["textContent"]="", !id_0141&&id_0142?id_0145=id_0146===id_0143["length"]-1?0:id_0146+1:id_0141&&(id_0145=id_0146===0?id_0143["length"]-1:id_0146-1), id_0145===id_0146)return;
    const id_0148=id_0143[id_0145];
    id_0148["setAttribute"]("aria-selected", !0),
    id_0142&&id_0142["setAttribute"]("aria-selected", !1),
    this["input"]["setAttribute"]("aria-activedescendant", id_0148.id)
  }
  selectOption()
  {
    const id_0149=this["querySelector"]("[aria-selected=\"true\"] a, button[aria-selected=\"true\"]");
    id_0149&&id_0149["click"]()
  }
  ["getSearchResults"](id_0150)
  {
    const id_0151=id_0150["replace"](" ", "-")["toLowerCase"]();
    if(this["setLiveRegionLoadingState"](), this["cachedResults"][id_0151])
    {
      this.renderSearchResults(this["cachedResults"][id_0151]);
      return
    }
    fetch(routes["predictive_search_url"]+"?q="+encodeURIComponent(id_0150)+"&section_id=predictive-search", {
      signal:this["abortController"].signal
    })["then"](id_0152=>{
      if(!id_0152.ok)
      {
        var id_0153=new Error(id_0152["status"]); throw this["close"](), id_0153
      }
      return id_0152["text"]()
    })["then"](id_0154=>{
      const id_0155=new DOMParser()["parseFromString"](id_0154, "text/html")["querySelector"]("#shopify-section-predictive-search")["innerHTML"]; this.allPredictiveSearchInstances["forEach"](id_0156=>{
        id_0156.cachedResults[id_0151]=id_0155
      }), this.renderSearchResults(id_0155)
    }).catch(id_0157=>{
      if(id_0157?.["code"]!==20)throw this["close"](), id_0157
    })
  }
  ["setLiveRegionLoadingState"]()
  {
    this["statusElement"]=this["statusElement"]||this["querySelector"](".predictive-search-status"),
    this["loadingText"]=this["loadingText"]||this["getAttribute"]("data-loading-text"),
    this["setLiveRegionText"](this["loadingText"]),
    this["setAttribute"]("loading", !0)
  }
  setLiveRegionText(id_0158)
  {
    this.statusElement.setAttribute("aria-hidden", "false"),
    this["statusElement"]["textContent"]=id_0158,
    setTimeout(()=>{
      this["statusElement"]["setAttribute"]("aria-hidden", "true")
    }, 1e3)
  }
  ["renderSearchResults"](id_0159)
  {
    this.predictiveSearchResults.innerHTML=id_0159,
    this["setAttribute"]("results", !0),
    this.setLiveRegionResults(),
    this["open"]()
  }
  ["setLiveRegionResults"]()
  {
    this.removeAttribute("loading"),
    this["setLiveRegionText"](this["querySelector"]("[data-predictive-search-live-region-count-value]").textContent)
  }
  ["getResultsMaxHeight"]()
  {
    return this["resultsMaxHeight"]=window["innerHeight"]-document["querySelector"](".section-header")["getBoundingClientRect"]()["bottom"],
    this["resultsMaxHeight"]
  }
  ["open"]()
  {
    this.predictiveSearchResults["style"]["maxHeight"]=this["resultsMaxHeight"]||this["getResultsMaxHeight"]()+"px",
    this.setAttribute("open", !0),
    this["input"].setAttribute("aria-expanded", !0),
    this["isOpen"]=!0
  }
  close(id_0160=!1)
  {
    this["closeResults"](id_0160),
    this.isOpen=!1
  }
  ["closeResults"](id_0161=!1)
  {
    id_0161&&(this["input"]["value"]="", this["removeAttribute"]("results"));
    const id_0162=this["querySelector"]("[aria-selected=\"true\"]");
    id_0162&&id_0162["setAttribute"]("aria-selected", !1),
    this["input"].setAttribute("aria-activedescendant", ""),
    this.removeAttribute("loading"),
    this["removeAttribute"]("open"),
    this["input"]["setAttribute"]("aria-expanded", !1),
    this["resultsMaxHeight"]=!1,
    this.predictiveSearchResults["removeAttribute"]("style")
  }
}
customElements["define"]("predictive-search", PredictiveSearch);
const defMed="dat"+"a-def"+"er";
class CartDrawer extends HTMLElement
{
  constructor()
  {
    super(),
    this["upsellHandles"]=this["getUpsellHandles"](),
    this["quantityGifts"]=document["querySelector"](".quantity-gifts"),
    this.checkForClear(),
    this["addEventListener"]("keyup", id_0163=>id_0163.code==="Escape"&&this["close"]()),
    this["querySelector"]("#CartDrawer-Overlay")["addEventListener"]("click", this["close"].bind(this)),
    this["setHeaderCartIconAccessibility"](),
    this["cartBodyScroll"]=0,
    this["countdownTimerPlayRequest"]=0,
    this["countdownTimerShouldPlay"]=!1,
    syncCartDrawerVisibleEmptyState(this)
  }
  ["setHeaderCartIconAccessibility"]()
  {
    let id_0164=document["querySelector"]("#cart-icon-bubble");
    if(!id_0164||(id_0164["tagName"]=="DIV"&&(id_0164=id_0164.closest(".header__icon--cart")), !id_0164))return;
    const id_0165=id_0164["closest"](".header__icons");
    id_0164["setAttribute"]("role", "button"),
    id_0164["setAttribute"]("aria-haspopup", "dialog"),
    id_0164.addEventListener("click", id_0166=>{
      id_0166["preventDefault"](), this.open(id_0164)
    }),
    this.oseid=id_0165.querySelector("form")["dataset"][this["dataset"]["type"]],
    id_0164["addEventListener"]("keydown", id_0167=>{
      id_0167["code"].toUpperCase()==="SPACE"&&(id_0167["preventDefault"](), this["open"](id_0164))
    })
  }
  ["open"](id_0168)
  {
    id_0168&&this["setActiveElement"](id_0168);
    const id_0169=this["querySelector"]('[id^="Details-"] summary');
    id_0169&&!id_0169.hasAttribute("role")&&this["setSummaryAccessibility"](id_0169),
    setTimeout(()=>{
      this["classList"].add("animate", "active")
    }),
    this.addEventListener("transitionend", ()=>{
      const id_0170=this["classList"]["contains"]("is-empty")?this.querySelector(".drawer__inner-empty"):document["getElementById"]("CartDrawer"), id_0171=this["querySelector"](".drawer__inner")||this.querySelector(".drawer__close"); trapFocus(id_0170, id_0171)
    }, {
      once:!0
    }),
    document.body["classList"]["add"]("overflow-hidden");
    this["playCountdownTimerWhenReady"]()
  }
  close()
  {
    this["cancelCountdownTimerPlay"](),
    this["classList"]["remove"]("active"),
    removeTrapFocus(this["activeElement"]),
    document["body"].classList.remove("overflow-hidden")
  }
  ["playCountdownTimerWhenReady"]()
  {
    const countdownTimer=this.querySelector("countdown-timer");
    if(!countdownTimer)return;
    this.countdownTimerShouldPlay=!0;
    const requestId=++this.countdownTimerPlayRequest;
    if(typeof countdownTimer.playTimer==="function")
    {
      countdownTimer.playTimer();
      return
    }
    if(!window.customElements||typeof customElements.whenDefined!=="function")return;
    customElements.whenDefined("countdown-timer").then(()=>{
      if(this.countdownTimerPlayRequest!==requestId||!this.countdownTimerShouldPlay||!this.isConnected||!countdownTimer.isConnected)return;
      if(this.querySelector("countdown-timer")!==countdownTimer||typeof countdownTimer.playTimer!=="function")return;
      countdownTimer.playTimer()
    }, ()=>{
    })
  }
  ["cancelCountdownTimerPlay"]()
  {
    this.countdownTimerShouldPlay=!1;
    this.countdownTimerPlayRequest+=1
  }
  disconnectedCallback()
  {
    this.cancelCountdownTimerPlay()
  }
  ["getUpsellHandles"]()
  {
    const id_0173=this["querySelectorAll"]("cart-drawer-upsell[data-toggle=\"true\"], cart-drawer-gift"),
    id_0174=[];
    return id_0173["forEach"](id_0175=>{
      id_0175.dataset["handle"]&&id_0174["push"](id_0175["dataset"]["handle"])
    }),
    id_0174
  }
  ["oneNonUpellRemaining"]()
  {
    return this.querySelectorAll(".cart-item:not(.cart-item--hidden)")["length"]<=1
  }
  checkForClear()
  {
    const id_0180=this["oneNonUpellRemaining"]();
    this["querySelectorAll"]("cart-remove-button").forEach(id_0181=>{
      id_0180?id_0181["clearCart"]=!0:id_0181["clearCart"]=!1
    })
  }
  ["setSummaryAccessibility"](id_0182)
  {
    id_0182["setAttribute"]("role", "button"),
    id_0182["setAttribute"]("aria-expanded", "false"),
    id_0182["nextElementSibling"]["getAttribute"]("id")&&id_0182["setAttribute"]("aria-controls", id_0182.nextElementSibling.id),
    id_0182["addEventListener"]("click", id_0183=>{
      id_0183.currentTarget["setAttribute"]("aria-expanded", !id_0183["currentTarget"]["closest"]("details")["hasAttribute"]("open"))
    }),
    id_0182["parentElement"]["addEventListener"]("keyup", onKeyUpEscape)
  }
  renderContents(id_0184, id_0185=!1)
  {
    this.querySelector(".drawer__inner").classList["contains"]("is-empty")&&this.querySelector(".drawer__inner")["classList"]["remove"]("is-empty"),
    this["productId"]=id_0184.id,
    this["getSectionsToRender"]()["forEach"](id_0186=>{
      const id_0187=id_0186["selector"]?document["querySelector"](id_0186["selector"]):document["getElementById"](id_0186.id); if(id_0187)
      {
        id_0187["innerHTML"]=this["getSectionInnerHTML"](id_0184["sections"][id_0186.id], id_0186["selector"]); const id_0188=id_0187.querySelector(".cart-drawer__body"); id_0188&&(id_0188["scrollTop"]=this["cartBodyScroll"])
      }
    }),
    syncCartDrawerVisibleEmptyState(this),
    this["checkForClear"]();
    const id_0189=this["querySelector"]("countdown-timer");
    id_0189&&id_0189.playTimer&&id_0189["playTimer"]();
    let id_0190=[],
    id_0191=[];
    this["querySelectorAll"]('cart-drawer-gift, cart-drawer-upsell[data-toggle="true"]')["forEach"](id_0192=>{
      id_0192["getUpdateRequired"]()&&(this["querySelector"](".cart-item--product-"+id_0192["dataset"]["handle"])?id_0192["dataset"]["selected"]==="false"&&id_0191["push"](id_0192):id_0192["dataset"]["selected"]==="true"&&id_0190.push(id_0192))
    });
    id_0190["sort"]((id_0193a, id_0193b)=>id_0193a["tagName"]==="CART-DRAWER-GIFT"?1:id_0193b["tagName"]==="CART-DRAWER-GIFT"?-1:0);
    const id_0190_designMode=window["Shopify"]&&window["Shopify"]["designMode"],
    id_0190_needsSync=!id_0190_designMode&&(id_0191["length"]>0||id_0190["length"]>0);
    if(id_0190_needsSync&&!id_0185){this._openAfterSync=!0}
    !id_0190_designMode&&id_0191["length"]>0?id_0191[0]["removeFromCart"]():!id_0190_designMode&&id_0190["length"]>0?id_0190[0]["addToCart"]():(this["quantityGifts"]&&this.quantityGifts["initUnlock"](), document["querySelectorAll"]("quantity-break-upsells")["forEach"](id_0193=>{
      id_0193.initUnlock()
    })),
    setTimeout(()=>{
      this["querySelector"]("#CartDrawer-Overlay")["addEventListener"]("click", this["close"]["bind"](this));
      if(id_0190_needsSync){return}
      if(this._openAfterSync){this._openAfterSync=false;this.open();document.dispatchEvent(new CustomEvent("cartDrawerOpened"));return}
      !id_0185&&this.open()
    })
  }
  ["getSectionInnerHTML"](id_0194, id_0195=".shopify-section")
  {
    let id_0196=parseSectionElement(id_0194, id_0195);
    return id_0195==="#CartDrawer"&&fixParsedHtml(this, id_0196),
    id_0196["innerHTML"]
  }
  ["getSectionsToRender"]()
  {
    return[{
      id:"cart-drawer",
      selector:"#CartDrawer"
    },
    {
      id:"cart-icon-bubble"
    }]
  }
  ["getSectionDOM"](id_0197, id_0198=".shopify-section")
  {
    return new DOMParser()["parseFromString"](id_0197, "text/html")["querySelector"](id_0198)
  }
  ["setActiveElement"](id_0199)
  {
    this["activeElement"]=id_0199
  }
}
customElements.define("cart-drawer", CartDrawer);
class CartDrawerItems extends CartItems
{
  constructor()
  {
    super(),
    this["cartDrawer"]=document["querySelector"]("cart-drawer"),
    this["secondCartItems"]=document["querySelector"]("cart-items")
  }
  ["getSectionInnerHTML"](id_0203, id_0204)
  {
    let id_0205=parseSectionElement(id_0203, id_0204);
    return id_0204===".drawer__inner"&&fixParsedHtml(this["cartDrawer"], id_0205),
    id_0205.innerHTML
  }
  getSectionsToRender()
  {
    return[{
      id:"CartDrawer",
      section:"cart-drawer",
      selector:".drawer__inner"
    },
    {
      id:"cart-icon-bubble",
      section:"cart-icon-bubble",
      selector:".shopify-section"
    }]
  }
}
customElements["define"]("cart-drawer-items", CartDrawerItems);
function fixParsedHtml(id_0206, id_0207)
{
  const id_0208=id_0206["querySelector"](".cart-drawer__body");
  if(id_0208)
  {
    const id_0209=document["querySelector"]("cart-drawer");
    id_0209.cartBodyScroll=id_0208["scrollTop"]
  }
  const id_0210=id_0207.querySelector(".cart-timer");
  if(id_0210)
  {
    const oldTimer=id_0206.querySelector(".cart-timer");
    oldTimer&&(id_0210.innerHTML=oldTimer["innerHTML"])
  }
  const id_0211=id_0206["querySelectorAll"]("cart-drawer-upsell[data-toggle=\"true\"], cart-drawer-gift");
  let id_0212=id_0207["querySelectorAll"]("cart-drawer-upsell[data-toggle=\"true\"], cart-drawer-gift");
  id_0211["forEach"]((id_0213, id_0214)=>{
    let id_0215=[...id_0212]["find"](id_0215a=>id_0215a["dataset"]["handle"]===id_0213["dataset"]["handle"]&&id_0215a["nodeName"]["toLowerCase"]()===id_0213["nodeName"]["toLowerCase"]())||id_0212[id_0214]; if(id_0215)
    {
      if(id_0213["nodeName"].toLowerCase()==="cart-drawer-upsell"&&(id_0215["dataset"]["selected"]=id_0213.dataset["selected"]), id_0215["dataset"].id=id_0213["dataset"].id, id_0215["querySelector"]("[name=\"id\"]")["value"]=id_0213["querySelector"]("[name=\"id\"]")["value"], id_0215["querySelector"](".upsell__image__img")&&(id_0215["querySelector"](".upsell__image__img")["src"]=id_0213["querySelector"](".upsell__image__img").src), id_0215["querySelector"](".upsell__variant-picker"))
      {
        const id_0216=id_0213["querySelectorAll"](".select__select"); id_0215["querySelectorAll"](".select__select").forEach((id_0217, id_0218)=>{
          id_0217["value"]=id_0216[id_0218]["value"], id_0217["querySelectorAll"]("option")["forEach"](id_0219=>{
            id_0219["removeAttribute"]("selected"), id_0219["value"]===id_0216[id_0218]["value"]["trim"]()&&id_0219["setAttribute"]("selected", "")
          })
        })
      }
      if(id_0213["dataset"]["updatePrices"]==="true")
      {
        var id_0220=id_0215["querySelector"](".upsell__price"), id_0221=id_0213["querySelector"](".upsell__price"); id_0220&&id_0221&&(id_0220.innerHTML=id_0221["innerHTML"])
      }
    }
  })
}
function getFocusableElements(id_0320)
{
  return Array.from(id_0320.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"))
}
document.querySelectorAll("[id^=\"Details-\"] summary")["forEach"](id_0321=>{
  id_0321["setAttribute"]("role", "button"), id_0321["setAttribute"]("aria-expanded", id_0321["parentNode"].hasAttribute("open")), id_0321["nextElementSibling"]?.["getAttribute"]("id")&&id_0321.setAttribute("aria-controls", id_0321.nextElementSibling.id), id_0321["addEventListener"]("click", id_0322=>{
    id_0322["currentTarget"]["setAttribute"]("aria-expanded", !id_0322.currentTarget.closest("details")["hasAttribute"]("open"))
  }), !id_0321["closest"]("header-drawer")&&id_0321["parentElement"]["addEventListener"]("keyup", onKeyUpEscape)
});
const trapFocusHandlers={
};
function trapFocus(id_0323, id_0324=id_0323)
{
  var id_0325=getFocusableElements(id_0323),
  id_0326=id_0325[0],
  id_0327=id_0325[id_0325["length"]-1];
  removeTrapFocus(),
  trapFocusHandlers.focusin=id_0328=>{
    id_0328.target!==id_0323&&id_0328["target"]!==id_0327&&id_0328["target"]!==id_0326||document["addEventListener"]("keydown", trapFocusHandlers.keydown)
  },
  trapFocusHandlers["focusout"]=function()
  {
    document["removeEventListener"]("keydown", trapFocusHandlers["keydown"])
  },
  trapFocusHandlers.keydown=function(id_0329)
  {
    id_0329["code"]["toUpperCase"]()==="TAB"&&(id_0329["target"]===id_0327&&!id_0329["shiftKey"]&&(id_0329["preventDefault"](), id_0326["focus"]()), (id_0329["target"]===id_0323||id_0329.target===id_0326)&&id_0329.shiftKey&&(id_0329.preventDefault(), id_0327["focus"]()))
  },
  document["addEventListener"]("focusout", trapFocusHandlers["focusout"]),
  document["addEventListener"]("focusin", trapFocusHandlers["focusin"]),
  id_0324["focus"](),
  id_0324["tagName"]==="INPUT"&&["search",
  "text",
  "email",
  "url"]["includes"](id_0324["type"])&&id_0324["value"]&&id_0324.setSelectionRange(0, id_0324.value["length"])
}
function pauseAllMedia()
{
  document["querySelectorAll"](".js-youtube")["forEach"](id_0330=>{
    id_0330.contentWindow.postMessage("{\"event\":\"command\",\"func\":\""+'pauseVideo","args":""}', "*")
  }),
  document["querySelectorAll"](".js-vimeo").forEach(id_0331=>{
    id_0331["contentWindow"]["postMessage"]("{\"method\":\"pause\"}", "*")
  }),
  document.querySelectorAll("media-gallery template video")["forEach"](id_0332=>id_0332["pause"]()),
  document["querySelectorAll"]("product-model")["forEach"](id_0333=>{
    id_0333["modelViewerUI"]&&id_0333.modelViewerUI["pause"]()
  })
}
function removeTrapFocus(id_0334=null)
{
  document["removeEventListener"]("focusin", trapFocusHandlers["focusin"]),
  document["removeEventListener"]("focusout", trapFocusHandlers.focusout),
  document["removeEventListener"]("keydown", trapFocusHandlers["keydown"]),
  id_0334&&id_0334["focus"]()
}
function onKeyUpEscape(id_0335)
{
  if(id_0335["code"].toUpperCase()!=="ESCAPE")return;
  const id_0336=id_0335["target"]["closest"]("details[open]");
  if(!id_0336)return;
  const id_0337=id_0336["querySelector"]("summary");
  id_0336["removeAttribute"]("open"),
  id_0337.setAttribute("aria-expanded", !1),
  id_0337["focus"]()
}
class QuantityInput extends HTMLElement
{
  constructor()
  {
    super(),
    this["input"]=this["querySelector"]("input"),
    this["changeEvent"]=new Event("change", {
      bubbles:!0
    }),
    this.quantityGifts=document.getElementById("quantity-gifts-"+this.dataset["section"]),
    this["input"]["addEventListener"]("change", this["onInputChange"]["bind"](this)),
    this.querySelectorAll("button").forEach(id_0338=>id_0338["addEventListener"]("click", this.onButtonClick["bind"](this)))
  }
  ["quantityUpdateUnsubscriber"]=void 0;
  connectedCallback()
  {
    this.validateQtyRules(),
    this["quantityUpdateUnsubscriber"]=subscribe(PUB_SUB_EVENTS.quantityUpdate, this["validateQtyRules"]["bind"](this))
  }
  ["disconnectedCallback"]()
  {
    this.quantityUpdateUnsubscriber&&this.quantityUpdateUnsubscriber()
  }
  ["onInputChange"](id_0339)
  {
    this.validateQtyRules()
  }
  onButtonClick(id_0340)
  {
    id_0340["preventDefault"]();
    const id_0341=this["input"]["value"],
    id_0342=id_0340["currentTarget"];
    id_0342&&id_0342["name"]==="plus"?this["input"]["stepUp"]():this["input"]["stepDown"](),
    id_0341!==this.input["value"]&&this["input"]["dispatchEvent"](this["changeEvent"])
  }
  validateQtyRules()
  {
    const id_0342=parseInt(this["input"]["value"]);
    if(this["input"]["min"])
    {
      const id_0343=parseInt(this["input"]["min"]);
      this["querySelector"](".quantity__button[name='minus']")["classList"]["toggle"]("disabled", id_0342<=id_0343)
    }
    if(this["input"]["max"])
    {
      const id_0344=parseInt(this.input["max"]);
      this["querySelector"](".quantity__button[name='plus']").classList["toggle"]("disabled", id_0342>=id_0344)
    }
    this["quantityGifts"]&&this.quantityGifts.unlockGifts&&this["quantityGifts"]["unlockGifts"](id_0342)
  }
}
customElements.define("quantity-input", QuantityInput);
function debounce(id_0345, id_0346)
{
  let id_0347;
  return(...id_0348)=>{
    clearTimeout(id_0347),
    id_0347=setTimeout(()=>id_0345["apply"](this, id_0348), id_0346)
  }
}
function fetchConfig(id_0349="json")
{
  return{
    method:"POST",
    headers:{
      "Content-Type":"application/json",
      Accept:"application/"+id_0349
    }
  }
}
function buildAjaxCartUrl(id_0350, id_0351)
{
  const id_0352=id_0350||id_0351,
  id_0353=id_0352.match(/[?#].*$/),
  id_0354=id_0353?id_0353[0]:"",
  id_0355=id_0354?id_0352.slice(0, -id_0354.length):id_0352,
  id_0356=id_0355.endsWith(".js")?id_0355:id_0355+".js";
  return id_0356+id_0354
}
function addDays(id_0350, id_0351)
{
  var id_0352=new Date(id_0350);
  return id_0352.setDate(id_0352["getDate"]()+id_0351),
  id_0352
}
function formatDates(id_0353, id_0354, id_0355=27)
{
  if(!id_0353||!id_0354)return;
  const id_0356=new Date(id_0354+"T00:00:00Z"),
  id_0357=id_0356["getFullYear"](),
  id_0358=id_0356["getMonth"](),
  id_0359=id_0356["getDate"](),
  id_0360=new Date(id_0357, id_0358, id_0359),
  id_0361=id_0353-id_0360;
  return Math.ceil(id_0361/(1e3*3600*24))<=id_0355
}
typeof window.Shopify=="undefined"&&(window["Shopify"]={
}),
Shopify["bind"]=function(id_0365, id_0366)
{
  return function()
  {
    return id_0365["apply"](id_0366, arguments)
  }
},
Shopify["setSelectorByValue"]=function(id_0367, id_0368)
{
  for(var id_0369=0, id_0370=id_0367.options.length; id_0369<id_0370; id_0369++)
  {
    var id_0371=id_0367["options"][id_0369];
    if(id_0368==id_0371["value"]||id_0368==id_0371["innerHTML"])return id_0367["selectedIndex"]=id_0369,
    id_0369
  }
},
Shopify["addListener"]=function(id_0372, id_0373, id_0374)
{
  id_0372["addEventListener"]?id_0372["addEventListener"](id_0373, id_0374, !1):id_0372["attachEvent"]("on"+id_0373, id_0374)
},
Shopify["postLink"]=function(id_0375, id_0376)
{
  id_0376=id_0376||{
  };
  var id_0377=id_0376.method||"post",
  id_0378=id_0376["parameters"]||{
  },
  id_0379=document["createElement"]("form");
  id_0379["setAttribute"]("method", id_0377),
  id_0379["setAttribute"]("action", id_0375);
  for(var id_0380 in id_0378)
  {
    var id_0381=document["createElement"]("input");
    id_0381["setAttribute"]("type", "hidden"),
    id_0381["setAttribute"]("name", id_0380),
    id_0381["setAttribute"]("value", id_0378[id_0380]),
    id_0379["appendChild"](id_0381)
  }
  document["body"].appendChild(id_0379),
  id_0379.submit(),
  document["body"]["removeChild"](id_0379)
},
Shopify["CountryProvinceSelector"]=function(id_0396, id_0397, id_0398)
{
  this["countryEl"]=document["getElementById"](id_0396),
  this.provinceEl=document["getElementById"](id_0397),
  this["provinceContainer"]=document["getElementById"](id_0398["hideElement"]||id_0397),
  Shopify["addListener"](this.countryEl, "change", Shopify["bind"](this["countryHandler"], this)),
  this["initCountry"](),
  this["initProvince"]()
},
Shopify["CountryProvinceSelector"].prototype={
  initCountry:function()
  {
    var id_0399=this["countryEl"]["getAttribute"]("data-default");
    Shopify.setSelectorByValue(this["countryEl"], id_0399),
    this["countryHandler"]()
  },
  initProvince:function()
  {
    var id_0400=this["provinceEl"].getAttribute("data-default");
    id_0400&&this.provinceEl["options"].length>0&&Shopify["setSelectorByValue"](this["provinceEl"], id_0400)
  },
  countryHandler:function(id_0401)
  {
    var id_0402=this["countryEl"]["options"][this["countryEl"]["selectedIndex"]],
    id_0403=id_0402["getAttribute"]("data-provinces"),
    id_0404=JSON["parse"](id_0403);
    if(this.clearOptions(this["provinceEl"]), id_0404&&id_0404["length"]==0)this["provinceContainer"].style["display"]="none";
    else{
      for(var id_0405=0; id_0405<id_0404.length; id_0405++)
      {
        var id_0402=document.createElement("option");
        id_0402["value"]=id_0404[id_0405][0],
        id_0402["innerHTML"]=id_0404[id_0405][1],
        this["provinceEl"]["appendChild"](id_0402)
      }
      this["provinceContainer"]["style"]["display"]=""
    }
  },
  clearOptions:function(id_0406)
  {
    for(; id_0406["firstChild"]; )id_0406["removeChild"](id_0406["firstChild"])
  },
  setOptions:function(id_0407, id_0408)
  {
    for(var id_0409=0, id_0410=id_0408.length; id_0409<id_0408["length"]; id_0409++)
    {
      var id_0411=document["createElement"]("option");
      id_0411["value"]=id_0408[id_0409],
      id_0411["innerHTML"]=id_0408[id_0409],
      id_0407["appendChild"](id_0411)
    }
  }
};
class InternalVideo extends HTMLElement
{
  constructor()
  {
    super();
    this.loaded=false;
    this.suspended=false;
    this.dragging=false;
    this.dragFrame=null;
    this.pendingDragClientX=null;
    this.listenersConnected=false;
    this.observer=null;
    this.onPlayButtonClick=this.playVideo.bind(this);
    this.onSoundButtonClick=this.toggleSound.bind(this);
    this.onEnded=this.endedVideo.bind(this);
    this.onPlay=this.handlePlay.bind(this);
    this.onPause=this.syncPlaybackState.bind(this);
    this.onVolumeChange=this.syncSoundState.bind(this);
    this.onWaiting=this.showSpinner.bind(this);
    this.onCanPlay=this.hideSpinner.bind(this);
    this.onTimelineUpdate=this.updateTimeline.bind(this);
    this.onTimelineKeydown=this.handleTimelineKeydown.bind(this);
    this.onTimelineClick=this.seekVideo.bind(this);
    this.onTimelineMouseDown=this.startDrag.bind(this);
    this.onTimelineTouchStart=this.startDrag.bind(this);
    this.onDocumentMouseMove=this.drag.bind(this);
    this.onDocumentMouseUp=this.stopDrag.bind(this);
    this.onDocumentTouchMove=this.drag.bind(this);
    this.onDocumentTouchEnd=this.stopDrag.bind(this);
    this.onWebkitBeginFullscreen=()=>this.syncFullscreenState(true);
    this.onWebkitEndFullscreen=()=>this.syncFullscreenState(false);
    this.onIntersection=this.handleIntersection.bind(this)
  }
  connectedCallback()
  {
    this.cacheElements();
    if(!this.video||this.listenersConnected)return;
    this.attachListeners();
    this.syncPlaybackState();
    this.syncSoundState();
    this.syncFullscreenState();
    if(this.timeline)this.setupTimelineA11y();
    this.setupAutoplayObserver()
  }
  disconnectedCallback()
  {
    this.detachListeners();
    this.stopDrag();
    if(this.observer)
    {
      this.observer.disconnect();
      this.observer=null
    }
  }
  cacheElements()
  {
    this.playButton=this.querySelector(".internal-video__play");
    this.soundButton=this.querySelector(".internal-video__sound-btn");
    this.fullscreenButton=this.querySelector(".internal-video__fullscreen-btn");
    this.timeline=this.querySelector(".internal-video__timeline");
    this.video=this.querySelector("video");
    this.noPlayBtn=this.dataset.noPlayBtn==="true";
    const invalidFormatSrc=this.video&&this.video.querySelector('source[type="application/x-mpegURL"]');
    if(invalidFormatSrc)invalidFormatSrc.remove()
  }
  attachListeners()
  {
    if(this.listenersConnected)return;
    if(this.playButton)this.playButton.addEventListener("click",this.onPlayButtonClick);
    if(this.soundButton)this.soundButton.addEventListener("click",this.onSoundButtonClick);
    this.video.addEventListener("ended",this.onEnded);
    this.video.addEventListener("play",this.onPlay);
    this.video.addEventListener("pause",this.onPause);
    this.video.addEventListener("volumechange",this.onVolumeChange);
    this.video.addEventListener("waiting",this.onWaiting);
    this.video.addEventListener("canplaythrough",this.onCanPlay);
    this.video.addEventListener("webkitbeginfullscreen",this.onWebkitBeginFullscreen);
    this.video.addEventListener("webkitendfullscreen",this.onWebkitEndFullscreen);
    if(this.timeline)
    {
      this.video.addEventListener("timeupdate",this.onTimelineUpdate);
      this.video.addEventListener("loadedmetadata",this.onTimelineUpdate);
      this.video.addEventListener("durationchange",this.onTimelineUpdate);
      this.video.addEventListener("emptied",this.onTimelineUpdate);
      this.timeline.addEventListener("keydown",this.onTimelineKeydown);
      this.timeline.addEventListener("click",this.onTimelineClick);
      this.timeline.addEventListener("mousedown",this.onTimelineMouseDown);
      this.timeline.addEventListener("touchstart",this.onTimelineTouchStart,{passive:false})
    }
    this.listenersConnected=true
  }
  detachListeners()
  {
    if(!this.listenersConnected||!this.video)return;
    if(this.playButton)this.playButton.removeEventListener("click",this.onPlayButtonClick);
    if(this.soundButton)this.soundButton.removeEventListener("click",this.onSoundButtonClick);
    this.video.removeEventListener("ended",this.onEnded);
    this.video.removeEventListener("play",this.onPlay);
    this.video.removeEventListener("pause",this.onPause);
    this.video.removeEventListener("volumechange",this.onVolumeChange);
    this.video.removeEventListener("waiting",this.onWaiting);
    this.video.removeEventListener("canplaythrough",this.onCanPlay);
    this.video.removeEventListener("webkitbeginfullscreen",this.onWebkitBeginFullscreen);
    this.video.removeEventListener("webkitendfullscreen",this.onWebkitEndFullscreen);
    if(this.timeline)
    {
      this.video.removeEventListener("timeupdate",this.onTimelineUpdate);
      this.video.removeEventListener("loadedmetadata",this.onTimelineUpdate);
      this.video.removeEventListener("durationchange",this.onTimelineUpdate);
      this.video.removeEventListener("emptied",this.onTimelineUpdate);
      this.timeline.removeEventListener("keydown",this.onTimelineKeydown);
      this.timeline.removeEventListener("click",this.onTimelineClick);
      this.timeline.removeEventListener("mousedown",this.onTimelineMouseDown);
      this.timeline.removeEventListener("touchstart",this.onTimelineTouchStart)
    }
    this.listenersConnected=false
  }
  playVideo()
  {
    if(!this.video)return;
    if(this.video.paused||this.video.ended)
    {
      if(!this.loaded)
      {
        this.video.load();
        this.loaded=true
      }
      this.classList.add("internal-video--playing");
      const playPromise=this.video.play();
      if(playPromise!==void 0)playPromise.then(()=>this.syncPlaybackState()).catch(()=>this.syncPlaybackState())
    }
    else
    {
      this.video.pause();
      this.syncPlaybackState()
    }
  }
  handlePlay()
  {
    this.hideSpinner();
    this.syncPlaybackState()
  }
  endedVideo()
  {
    this.syncPlaybackState()
  }
  syncPlaybackState()
  {
    if(!this.video)return;
    const isPlaying=!this.video.paused&&!this.video.ended;
    this.classList.toggle("internal-video--playing",isPlaying);
    if(!this.playButton)return;
    const playLabel=this.playButton.dataset.playLabel||"Play video";
    const pauseLabel=this.playButton.dataset.pauseLabel||"Pause video";
    this.playButton.setAttribute("aria-label",isPlaying?pauseLabel:playLabel);
    this.playButton.setAttribute("aria-pressed",String(isPlaying));
    if(this.noPlayBtn)this.playButton.style.visibility=isPlaying?"hidden":"visible"
  }
  toggleSound()
  {
    if(!this.video)return;
    this.video.muted=!this.video.muted;
    this.syncSoundState()
  }
  syncSoundState()
  {
    if(!this.video)return;
    const isMuted=this.video.muted;
    this.classList.toggle("internal-video--muted",isMuted);
    if(!this.soundButton)return;
    const muteLabel=this.soundButton.dataset.muteLabel||"Mute video";
    const unmuteLabel=this.soundButton.dataset.unmuteLabel||"Unmute video";
    this.soundButton.setAttribute("aria-label",isMuted?unmuteLabel:muteLabel);
    this.soundButton.setAttribute("aria-pressed",String(!isMuted))
  }
  syncFullscreenState(forcedState)
  {
    const nativeFullscreen=document.fullscreenElement===this||this.video&&this.video.webkitDisplayingFullscreen===true;
    const isFullscreen=typeof forcedState==="boolean"?forcedState:this.classList.contains("internal-video--modal-open")||nativeFullscreen;
    this.classList.toggle("internal-video--fullscreen",isFullscreen);
    if(!this.fullscreenButton)return;
    const enterLabel=this.fullscreenButton.dataset.enterFullscreenLabel||"Enter fullscreen";
    const exitLabel=this.fullscreenButton.dataset.exitFullscreenLabel||"Exit fullscreen";
    this.fullscreenButton.setAttribute("aria-label",isFullscreen?exitLabel:enterLabel);
    this.fullscreenButton.setAttribute("aria-pressed",String(isFullscreen))
  }
  setupTimelineA11y()
  {
    this.timeline.hasAttribute("role")||this.timeline.setAttribute("role","slider");
    this.timeline.hasAttribute("tabindex")||(this.timeline.tabIndex=0);
    this.timeline.hasAttribute("aria-label")||this.timeline.hasAttribute("aria-labelledby")||this.timeline.setAttribute("aria-label",this.timeline.dataset.label||"Video progress");
    this.timeline.setAttribute("aria-orientation","horizontal");
    this.timeline.setAttribute("aria-valuemin","0");
    this.updateTimeline()
  }
  getVideoDuration()
  {
    const duration=Number(this.video.duration);
    return Number.isFinite(duration)&&duration>0?duration:0
  }
  formatTimelineTime(value)
  {
    const seconds=Math.max(0,Math.round(value));
    const hours=Math.floor(seconds/3600);
    const minutes=Math.floor(seconds%3600/60);
    const remainingSeconds=seconds%60;
    const paddedSeconds=String(remainingSeconds).padStart(2,"0");
    return hours>0?hours+":"+String(minutes).padStart(2,"0")+":"+paddedSeconds:minutes+":"+paddedSeconds
  }
  syncTimelineA11y(duration,currentTime)
  {
    const roundedDuration=Math.round(duration*1000)/1000;
    const roundedCurrent=Math.min(Math.round(currentTime*1000)/1000,roundedDuration);
    this.timeline.setAttribute("aria-valuemax",String(roundedDuration));
    this.timeline.setAttribute("aria-valuenow",String(roundedCurrent));
    this.timeline.setAttribute("aria-valuetext",this.formatTimelineTime(roundedCurrent)+" / "+this.formatTimelineTime(roundedDuration));
    if(roundedDuration)this.timeline.removeAttribute("aria-disabled");
    else this.timeline.setAttribute("aria-disabled","true")
  }
  setCurrentTime(value)
  {
    const duration=this.getVideoDuration();
    if(!duration)return;
    const nextTime=Math.min(duration,Math.max(0,Number(value)||0));
    try
    {
      this.video.currentTime=nextTime
    }
    catch{}
    this.updateTimeline()
  }
  handleTimelineKeydown(event)
  {
    const duration=this.getVideoDuration();
    if(!duration)return;
    const isRtl=getComputedStyle(this.timeline).direction==="rtl";
    const step=5;
    let nextTime;
    if(event.key==="Home")nextTime=0;
    else if(event.key==="End")nextTime=duration;
    else if(event.key==="ArrowLeft")nextTime=this.video.currentTime+(isRtl?step:-step);
    else if(event.key==="ArrowRight")nextTime=this.video.currentTime+(isRtl?-step:step);
    else if(event.key==="ArrowUp"||event.key==="PageUp")nextTime=this.video.currentTime+step;
    else if(event.key==="ArrowDown"||event.key==="PageDown")nextTime=this.video.currentTime-step;
    else return;
    event.preventDefault();
    this.setCurrentTime(nextTime)
  }
  updateTimeline()
  {
    if(!this.timeline||!this.video)return;
    const duration=this.getVideoDuration();
    const currentTime=duration?Math.min(duration,Math.max(0,Number(this.video.currentTime)||0)):0;
    const completed=duration?currentTime/duration*100:0;
    this.style.setProperty("--completed",completed+"%");
    this.syncTimelineA11y(duration,currentTime)
  }
  startDrag(event)
  {
    if(!this.timeline)return;
    if(event.type==="mousedown"&&event.button!==0)return;
    this.timeline.focus({preventScroll:true});
    if(event.cancelable)event.preventDefault();
    this.dragging=true;
    document.addEventListener("mousemove",this.onDocumentMouseMove);
    document.addEventListener("mouseup",this.onDocumentMouseUp);
    document.addEventListener("touchmove",this.onDocumentTouchMove,{passive:false});
    document.addEventListener("touchend",this.onDocumentTouchEnd);
    document.addEventListener("touchcancel",this.onDocumentTouchEnd);
    this.drag(event)
  }
  stopDrag()
  {
    this.dragging=false;
    if(this.dragFrame!==null)
    {
      cancelAnimationFrame(this.dragFrame);
      this.dragFrame=null
    }
    this.pendingDragClientX=null;
    document.removeEventListener("mousemove",this.onDocumentMouseMove);
    document.removeEventListener("mouseup",this.onDocumentMouseUp);
    document.removeEventListener("touchmove",this.onDocumentTouchMove);
    document.removeEventListener("touchend",this.onDocumentTouchEnd);
    document.removeEventListener("touchcancel",this.onDocumentTouchEnd)
  }
  drag(event)
  {
    if(!this.dragging)return;
    if(event.cancelable)event.preventDefault();
    const pointer=event.touches&&event.touches.length?event.touches[0]:event.changedTouches&&event.changedTouches.length?event.changedTouches[0]:event;
    this.pendingDragClientX=pointer.clientX;
    if(this.dragFrame!==null)return;
    this.dragFrame=requestAnimationFrame(()=>{
      this.dragFrame=null;
      if(!this.dragging||this.pendingDragClientX===null)return;
      this.seekVideo({clientX:this.pendingDragClientX})
    })
  }
  seekVideo(event)
  {
    if(!this.timeline)return;
    const bounds=this.timeline.getBoundingClientRect();
    if(!bounds.width)return;
    let ratio=Math.min(1,Math.max(0,(event.clientX-bounds.left)/bounds.width));
    if(getComputedStyle(this.timeline).direction==="rtl")ratio=1-ratio;
    this.setCurrentTime(ratio*this.getVideoDuration())
  }
  showSpinner()
  {
    this.classList.add("internal-video--loading")
  }
  hideSpinner()
  {
    this.classList.remove("internal-video--loading")
  }
  setupAutoplayObserver()
  {
    if(this.dataset.autoplay!=="true"||this.loaded||!("IntersectionObserver"in window))return;
    this.observer=new IntersectionObserver(this.onIntersection,{root:null,rootMargin:"0px",threshold:.05});
    this.observer.observe(this)
  }
  handleIntersection(entries)
  {
    entries.forEach(entry=>{
      if(!entry.isIntersecting)return;
      for(const source of this.video.querySelectorAll("source[data-src]"))
      {
        source.setAttribute("src",source.getAttribute("data-src"));
        source.removeAttribute("data-src")
      }
      this.loaded=true;
      this.video.load();
      const playPromise=this.video.play();
      if(playPromise!==void 0)playPromise.catch(()=>this.syncPlaybackState());
      if(this.observer)
      {
        this.observer.disconnect();
        this.observer=null
      }
    })
  }
}
customElements.define("internal-video",InternalVideo);
const internalVideoModalRegistry=new WeakMap;
function openInternalVideoModal(internalVideo)
{
  const openVideo=document.querySelector("internal-video.internal-video--modal-open");
  if(openVideo&&openVideo!==internalVideo)closeInternalVideoModal(openVideo);
  if(internalVideoModalRegistry.has(internalVideo)||!internalVideo.parentNode)return;
  const placeholder=document.createComment("internal-video-modal-placeholder");
  const overlay=document.createElement("div");
  const panel=document.createElement("div");
  const internalVideoStyles=getComputedStyle(internalVideo);
  const modalRadius=internalVideoStyles.getPropertyValue("--internal-video-modal-radius").trim()||internalVideoStyles.borderRadius;
  const returnFocus=document.activeElement instanceof HTMLElement?document.activeElement:null;
  const fullscreenButton=internalVideo.querySelector(".internal-video__fullscreen-btn");
  overlay.className="internal-video-modal";
  overlay.setAttribute("role","dialog");
  overlay.setAttribute("aria-modal","true");
  overlay.setAttribute("aria-label",internalVideo.dataset.dialogLabel||fullscreenButton&&fullscreenButton.dataset.dialogLabel||"Video player");
  panel.className="internal-video-modal__panel";
  if(modalRadius)panel.style.setProperty("--internal-video-modal-radius",modalRadius);
  internalVideo.parentNode.insertBefore(placeholder,internalVideo);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  panel.appendChild(internalVideo);
  internalVideo.classList.add("internal-video--modal-open");
  internalVideo.syncFullscreenState(true);
  document.documentElement.classList.add("internal-video-modal-open");
  document.body.classList.add("internal-video-modal-open");
  const closeOnOverlayClick=event=>{
    if(event.target===overlay)closeInternalVideoModal(internalVideo)
  };
  overlay.addEventListener("click",closeOnOverlayClick);
  internalVideoModalRegistry.set(internalVideo,{placeholder,overlay,closeOnOverlayClick,returnFocus});
  if(fullscreenButton)trapFocus(overlay,fullscreenButton);
  else overlay.setAttribute("tabindex","-1"),overlay.focus()
}
function closeInternalVideoModal(internalVideo)
{
  const state=internalVideoModalRegistry.get(internalVideo);
  if(!state)return;
  if(state.overlay)state.overlay.removeEventListener("click",state.closeOnOverlayClick);
  removeTrapFocus();
  if(state.placeholder&&state.placeholder.parentNode)state.placeholder.parentNode.insertBefore(internalVideo,state.placeholder);
  else document.body.appendChild(internalVideo);
  if(state.placeholder&&state.placeholder.parentNode)state.placeholder.parentNode.removeChild(state.placeholder);
  if(state.overlay)state.overlay.remove();
  internalVideo.classList.remove("internal-video--modal-open");
  internalVideo.syncFullscreenState(false);
  internalVideoModalRegistry.delete(internalVideo);
  if(!document.querySelector("internal-video.internal-video--modal-open"))
  {
    document.documentElement.classList.remove("internal-video-modal-open");
    document.body.classList.remove("internal-video-modal-open")
  }
  const focusTarget=state.returnFocus&&state.returnFocus.isConnected?state.returnFocus:internalVideo.querySelector(".internal-video__fullscreen-btn");
  if(focusTarget)focusTarget.focus({preventScroll:true})
}
function toggleInternalVideoFullscreen(internalVideo)
{
  if(document.fullscreenElement===internalVideo)
  {
    const exitPromise=document.exitFullscreen&&document.exitFullscreen();
    if(exitPromise&&typeof exitPromise.catch==="function")exitPromise.catch(()=>internalVideo.syncFullscreenState());
    return
  }
  if(internalVideo.requestFullscreen)
  {
    const requestPromise=internalVideo.requestFullscreen();
    if(requestPromise&&typeof requestPromise.catch==="function")requestPromise.catch(()=>internalVideo.syncFullscreenState());
    return
  }
  const video=internalVideo.querySelector("video");
  if(video&&video.webkitEnterFullscreen)video.webkitEnterFullscreen()
}
document.addEventListener("click",event=>{
  const button=event.target.closest(".internal-video__fullscreen-btn");
  const internalVideo=button?button.closest("internal-video"):event.target.closest("internal-video");
  if(!internalVideo)return;
  if(!button)
  {
    if(internalVideo.dataset.fullscreenMode!=="modal")return;
    const skippedControl=event.target.closest(".internal-video__sound-btn,.internal-video__timeline,a,input,select,textarea,label,summary");
    if(skippedControl||internalVideoModalRegistry.has(internalVideo)||!internalVideo.querySelector(".internal-video__fullscreen-btn"))return;
    event.preventDefault();
    event.stopPropagation();
    openInternalVideoModal(internalVideo);
    return
  }
  event.preventDefault();
  event.stopPropagation();
  if(internalVideo.dataset.fullscreenMode==="modal")
  {
    if(internalVideoModalRegistry.has(internalVideo))closeInternalVideoModal(internalVideo);
    else openInternalVideoModal(internalVideo)
  }
  else toggleInternalVideoFullscreen(internalVideo)
});
document.addEventListener("fullscreenchange",()=>{
  document.querySelectorAll("internal-video").forEach(internalVideo=>internalVideo.syncFullscreenState())
});
document.addEventListener("keydown",event=>{
  if(event.key!=="Escape")return;
  const openVideo=document.querySelector("internal-video.internal-video--modal-open");
  if(!openVideo)return;
  event.preventDefault();
  closeInternalVideoModal(openVideo)
});
class ComparisonSlider extends HTMLElement
{
  constructor()
  {
    super(),
    this.sliderOverlay=this["querySelector"](".comparison-slider__overlay"),
    this["sliderLine"]=this["querySelector"](".comparison-slider__line"),
    this["sliderInput"]=this["querySelector"](".comparison-slider__input"),
    this["sliderInput"]["addEventListener"]("input", this["handleChange"].bind(this))
  }
  ["handleChange"](id_0427)
  {
    const id_0428=id_0427.currentTarget["value"];
    this.sliderOverlay.style.width=id_0428+"%",
    this["sliderLine"].style["left"]=id_0428+"%",
    this["style"]["setProperty"]("--position", id_0428+"%")
  }
}
customElements["define"]("comparison-slider", ComparisonSlider);
class PromoPopup extends HTMLElement
{
  constructor()
  {
    super();
    this.boundClick=this.handleClick.bind(this);
    this.boundKeydown=this.handleKeydown.bind(this)
  }
  connectedCallback()
  {
    if(this.controllerConnected)return;
    this.controllerConnected=true;
    this.testMode=this.dataset.testMode==="true";
    this.secondsDelay=this.dataset.delaySeconds;
    this.daysFrequency=this.dataset.delayDays;
    this.modal=this.querySelector(".sign-up-popup-modal");
    this.timer=this.querySelector(".popup-modal__timer");
    this.timerDuration=this.dataset.timerDuration;
    this.timerKey=this.dataset.timerKey;
    this.minutesSpan=this.querySelector(".popup-modal__timer__minutes");
    this.secondsSpan=this.querySelector(".popup-modal__timer__seconds");
    this.timerConnectAttempts=0;
    this.timerConnectRetry=null;
    this.timerRegistrationId=null;
    this.timerExpired=false;
    this.overlay=this.querySelector(".sign-up-popup-overlay");
    this.storageKey="promo-bar-data-"+window.location.host;
    this.addEventListener("click", this.boundClick);
    document.addEventListener("keydown", this.boundKeydown);
    this.connectTimer();
    if(!this.modal)return;
    if(this.modal.classList.contains("popup-modal--active"))
    {
      this.openPopup(false);
      return
    }
    this.setOpenState(false);
    if(this.testMode)
    {
      this.timer&&this.displayPromoTimer();
      return
    }
    this.scheduleStoredPopup()
  }
  scheduleStoredPopup()
  {
    let storedValue=null;
    try
    {
      storedValue=localStorage.getItem(this.storageKey)
    }
    catch(error)
    {
      storedValue=null
    }
    if(storedValue===null)
    {
      this.openPopupModal();
      return
    }
    try
    {
      const storedData=JSON.parse(storedValue),
      nextDisplayDate=new Date(storedData.next_display_date);
      if(currentDate.getTime()>nextDisplayDate.getTime())this.openPopupModal()
    }
    catch(error)
    {
      this.openPopupModal()
    }
  }
  openPopupModal()
  {
    this.clearOpenTimeout();
    const requestedDelay=parseInt(this.secondsDelay)*1e3+3e3,
    delay=Number.isFinite(requestedDelay)?Math.max(0, requestedDelay):0;
    this.openTimeout=setTimeout(()=>{
      this.openTimeout=null;
      this.openPopup(true)
    }, delay)
  }
  openPopup(persistDisplay)
  {
    if(!this.modal||!this.isConnected)return;
    this.beginModalSession();
    this.setOpenState(true);
    if(persistDisplay)this.persistNextDisplay();
    this.timer&&this.displayPromoTimer();
    this.focusEntryControl()
  }
  persistNextDisplay()
  {
    const nextDisplayDate=addDays(currentDate, parseInt(this.daysFrequency)),
    storageData={next_display_date:nextDisplayDate, dismissed:false};
    try
    {
      localStorage.setItem(this.storageKey, JSON.stringify(storageData))
    }
    catch(error)
    {
      return
    }
  }
  clearOpenTimeout()
  {
    if(!this.openTimeout)return;
    clearTimeout(this.openTimeout);
    this.openTimeout=null
  }
  displayPromoTimer()
  {
    this.connectTimer()
  }
  connectTimer()
  {
    if(!this.timer||this.timerRegistrationId||this.timerExpired)return;
    const durationMinutes=Number.parseFloat(this.timerDuration);
    if(!Number.isFinite(durationMinutes)||durationMinutes<=0)return;
    const engine=window.sessionCountdownEngine;
    if(!engine||typeof engine.register!=="function")
    {
      if(this.timerConnectAttempts>=20)return;
      this.timerConnectAttempts+=1;
      clearTimeout(this.timerConnectRetry);
      this.timerConnectRetry=setTimeout(()=>{
        this.timerConnectRetry=null;
        if(this.isConnected)this.connectTimer()
      }, 50);
      return
    }
    clearTimeout(this.timerConnectRetry);
    this.timerConnectRetry=null;
    const maximumSafeDurationSeconds=Math.max(1, Math.floor((Number.MAX_SAFE_INTEGER-Date.now())/1000)),
    requestedDurationSeconds=durationMinutes*60,
    durationSeconds=Math.min(Number.isFinite(requestedDurationSeconds)?Math.max(1, Math.round(requestedDurationSeconds)):maximumSafeDurationSeconds, maximumSafeDurationSeconds),
    timerKey=this.timerKey||"promo-popup-legacy:"+(this.dataset.sectionId||"unknown")+":"+durationMinutes,
    registrationId=engine.register(timerKey, durationSeconds, remainingSeconds=>this.updateTimer(remainingSeconds), ()=>{
      this.timerExpired=true;
      this.timerRegistrationId=null;
      this.updateTimer(0);
      this.timer.style.display="none"
    });
    if(!this.timerExpired)this.timerRegistrationId=registrationId
  }
  disconnectTimer()
  {
    clearTimeout(this.timerConnectRetry);
    this.timerConnectRetry=null;
    const engine=window.sessionCountdownEngine;
    if(this.timerRegistrationId&&engine&&typeof engine.unregister==="function")engine.unregister(this.timerRegistrationId);
    this.timerRegistrationId=null
  }
  updateTimer(totalSeconds)
  {
    if(!this.minutesSpan||!this.secondsSpan)return;
    const safeSeconds=Math.max(0, Math.floor(Number(totalSeconds)||0)),
    minutes=Math.floor(safeSeconds/60).toString().padStart(2, "0"),
    seconds=Math.floor(safeSeconds%60).toString().padStart(2, "0");
    this.minutesSpan.textContent=minutes,
    this.secondsSpan.textContent=seconds
  }
  setOpenState(open)
  {
    if(this.overlay)
    {
      this.overlay.classList.toggle("popup-overlay--active", open&&this.isOverlayEnabled());
      this.overlay.setAttribute("aria-hidden", "true")
    }
    if(!this.modal)return;
    this.modal.classList.toggle("popup-modal--active", open);
    this.modal.setAttribute("aria-hidden", open?"false":"true");
    this.modal.toggleAttribute("inert", !open)
  }
  isOverlayEnabled()
  {
    const setting=this.dataset.showOverlay!==undefined?this.dataset.showOverlay:this.dataset.displayOverlay!==undefined?this.dataset.displayOverlay:this.dataset.overlay;
    return setting!=="false"
  }
  handleClick(event)
  {
    if(!(event.target instanceof Element))return;
    if(event.target===this.overlay)
    {
      if(this.isOverlayEnabled())this.closeModal(true);
      return
    }
    const closeControl=event.target.closest(".promp-popup__close-btn");
    if(closeControl&&this.contains(closeControl))this.closeModal(true)
  }
  handleKeydown(event)
  {
    if(!this.modal||!this.modal.classList.contains("popup-modal--active"))return;
    if(event.key==="Tab")
    {
      this.trapFocus(event);
      return
    }
    if(event.key!=="Escape")return;
    event.preventDefault();
    this.closeModal(true)
  }
  getFocusableControls()
  {
    if(!this.modal)return[];
    const selector=[
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type=\"hidden\"])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex=\"-1\"])"
    ].join(",");
    return Array.from(this.modal.querySelectorAll(selector)).filter(control=>!control.closest("[inert]")&&control.getClientRects().length>0)
  }
  trapFocus(event)
  {
    const controls=this.getFocusableControls();
    if(controls.length===0)
    {
      event.preventDefault();
      this.modal.focus();
      return
    }
    const firstControl=controls[0],
    lastControl=controls[controls.length-1];
    if(!this.modal.contains(document.activeElement))
    {
      event.preventDefault();
      firstControl.focus()
    }
    else if(event.shiftKey&&document.activeElement===firstControl)
    {
      event.preventDefault();
      lastControl.focus()
    }
    else if(!event.shiftKey&&document.activeElement===lastControl)
    {
      event.preventDefault();
      firstControl.focus()
    }
  }
  focusEntryControl()
  {
    if(!this.modal)return;
    const control=this.modal.querySelector("[autofocus],.promo-popup-legacy__close")||this.getFocusableControls()[0]||this.modal;
    clearTimeout(this.focusTimer);
    this.focusTimer=null;
    let attempts=0;
    const focusControl=()=>{
      this.focusTimer=null;
      if(!this.modal.classList.contains("popup-modal--active")||this.modal.contains(document.activeElement))return;
      control.focus({preventScroll:true});
      attempts+=1;
      if(document.activeElement!==control&&attempts<8)this.focusTimer=setTimeout(focusControl, 25)
    };
    focusControl()
  }
  beginModalSession()
  {
    if(this.modalSessionActive)return;
    this.modalSessionActive=true;
    if(this.focusRestoreFrame)
    {
      cancelAnimationFrame(this.focusRestoreFrame);
      this.focusRestoreFrame=null
    }
    this.previouslyFocusedElement=document.activeElement instanceof HTMLElement?document.activeElement:null;
    if(!document.body)return;
    this.bodyStyleSnapshot={
      overflow:document.body.style.overflow,
      paddingRight:document.body.style.paddingRight
    };
    const scrollbarWidth=Math.max(0, window.innerWidth-document.documentElement.clientWidth);
    if(scrollbarWidth>0)
    {
      const currentPadding=parseFloat(window.getComputedStyle(document.body).paddingRight)||0;
      document.body.style.paddingRight=currentPadding+scrollbarWidth+"px"
    }
    document.body.style.overflow="hidden"
  }
  endModalSession(restoreFocus)
  {
    if(!this.modalSessionActive)return;
    this.modalSessionActive=false;
    if(document.body&&this.bodyStyleSnapshot)
    {
      document.body.style.overflow=this.bodyStyleSnapshot.overflow;
      document.body.style.paddingRight=this.bodyStyleSnapshot.paddingRight
    }
    this.bodyStyleSnapshot=null;
    const focusTarget=this.previouslyFocusedElement;
    this.previouslyFocusedElement=null;
    if(restoreFocus&&focusTarget&&focusTarget.isConnected)
    {
      this.focusRestoreFrame=requestAnimationFrame(()=>{
        this.focusRestoreFrame=null;
        focusTarget.focus()
      })
    }
  }
  closeModal(restoreFocus=true)
  {
    this.clearOpenTimeout();
    clearTimeout(this.focusTimer);
    this.focusTimer=null;
    this.setOpenState(false);
    this.endModalSession(restoreFocus)
  }
  disconnectedCallback()
  {
    if(!this.controllerConnected)return;
    this.removeEventListener("click", this.boundClick);
    document.removeEventListener("keydown", this.boundKeydown);
    this.disconnectTimer();
    this.closeModal(false);
    if(this.focusRestoreFrame)
    {
      cancelAnimationFrame(this.focusRestoreFrame);
      this.focusRestoreFrame=null
    }
    this.controllerConnected=false
  }
}
customElements.define("promo-popup", PromoPopup);
class SectionsGroup extends HTMLElement
{
  constructor()
  {
    super(),
    this["sectionOneContainer"]=this["querySelector"](".section-group__section-one-container"),
    this["sectionTwoContainer"]=this.querySelector(".section-group__section-two-container"),
    this.transferSections()
  }
  ["transferSections"]()
  {
    this["sectionOne"]=document.querySelector(this["dataset"]["sectionOneId"]+" .content-for-grouping"),
    this["sectionTwo"]=document["querySelector"](this.dataset.sectionTwoId+" .content-for-grouping"),
    this["sectionOne"]&&!this.sectionOneContainer["childNodes"].length&&this.sectionOneContainer["appendChild"](this.sectionOne),
    this["sectionTwo"]&&!this["sectionTwoContainer"]["childNodes"]["length"]&&this["sectionTwoContainer"]["appendChild"](this["sectionTwo"])
  }
}
customElements.define("section-group", SectionsGroup);
class ClickableDiscount extends HTMLElement
{
  constructor()
  {
    super(),
    this["button"]=this["querySelector"](".clickable-discount__btn"),
    this["button"]["addEventListener"]("click", this.handleClick["bind"](this)),
    this["dataset"]["applied"]==="true"?this["handleClick"]():this["reapplyDiscountIfApplicable"]()
  }
  ["handleClick"]()
  {
    this["dataset"]["loading"]="true",
    this.button["disabled"]=!0,
    this["dataset"]["error"]="false",
    fetch("/discount/"+this.dataset["code"])["then"](id_0440=>{
      if(!id_0440.ok)throw new Error("Error"); this["dataset"]["applied"]="true", sessionStorage["setItem"]("discount-"+this["dataset"]["code"]+"-applied", "true")
    }).catch(id_0441=>{
      this["dataset"]["error"]="true", this["button"]["disabled"]=!1
    })["finally"](()=>{
      this.dataset["loading"]="false"
    })
  }
  ["reapplyDiscountIfApplicable"]()
  {
    const id_0442=this["dataset"].code;
    sessionStorage["getItem"]("discount-"+id_0442+"-applied")&&(this.dataset["applied"]="true", this["button"].disabled=!0, setTimeout(()=>{
      fetch("/discount/"+id_0442)["catch"](id_0443=>{
        this["dataset"]["applied"]="false", this["button"].disabled=!1
      })
    }, 3e3))
  }
}
customElements["define"]("clickable-discount", ClickableDiscount);
class DynamicDates extends HTMLElement
{
  constructor()
  {
    super(),
    this["dateFormat"]=this["dataset"]["dateFormat"],
    this["days"]=this["rearrangeDays"](this["dataset"]["dayLabels"].split(",")),
    this["months"]=this["dataset"].monthLabels["split"](","),
    this["elementsToChange"]=this["querySelectorAll"]("[data-dynamic-date=\"true\"]"),
    this.daysToSkip=this["parseDaysToSkip"](this["dataset"].daysToSkip||""),
    this.datesToSkip=this["parseDatesToSkip"](this.dataset.datesToSkip||""),
    this.insertDates()
  }
  addDaysSkipping(id_0445, id_0446)
  {
    let id_0447=new Date(id_0445),
    id_0448=Number(id_0446)||0;
    for(; id_0448>0; )id_0447=addDays(id_0447, 1),
    this["isSkipped"](id_0447)||id_0448--;
    return id_0447
  }
  ["parseDaysToSkip"](id_0449)
  {
    const id_0450=new Map([["sunday", 0], ["monday", 1], ["tuesday", 2], ["wednesday", 3], ["thursday", 4], ["friday", 5], ["saturday", 6]]),
    id_0451=new Set;
    return id_0449&&id_0449["split"](",")["forEach"](id_0452=>{
      const id_0453=id_0452.trim(); id_0450["has"](id_0453)&&id_0451.add(id_0450["get"](id_0453))
    }),
    id_0451
  }
  parseDatesToSkip(id_0454)
  {
    const id_0455=new Set;
    return id_0454&&id_0454.split(",")["forEach"](id_0456=>{
      const id_0457=id_0456["trim"](); if(!id_0457)return; const id_0458=id_0457.split("-"); if(id_0458.length===2)
      {
        const id_0459=String(Math["max"](1, Math.min(12, Number(id_0458[0]))))["padStart"](2, "0"), id_0460=String(Math.max(1, Math["min"](31, Number(id_0458[1]))))["padStart"](2, "0"); id_0455["add"](id_0459+"-"+id_0460)
      }
      else if(id_0458["length"]===3)
      {
        const id_0461=String(Math["max"](1, Math["min"](12, Number(id_0458[1]))))["padStart"](2, "0"), id_0462=String(Math["max"](1, Math.min(31, Number(id_0458[2]))))["padStart"](2, "0"); id_0455["add"](id_0461+"-"+id_0462)
      }
    }),
    id_0455
  }
  ["isSkipped"](id_0463)
  {
    const id_0464=this["daysToSkip"].has(id_0463["getDay"]()),
    id_0465=String(id_0463["getMonth"]()+1)["padStart"](2, "0"),
    id_0466=String(id_0463.getDate()).padStart(2, "0"),
    id_0467=this.datesToSkip["has"](id_0465+"-"+id_0466);
    return id_0464||id_0467
  }
  ["nextValidDate"](id_0468)
  {
    let id_0469=new Date(id_0468);
    for(; this["isSkipped"](id_0469); )id_0469=addDays(id_0469, 1);
    return id_0469
  }
  ["insertDates"]()
  {
    this["elementsToChange"].forEach(id_0470=>{
      const id_0471=id_0470["dataset"]["text"], id_0472=parseInt(id_0470["dataset"]["minDays"]), id_0473=parseInt(id_0470.dataset["maxDays"]), id_0474=this.addDaysSkipping(currentDate, id_0472), id_0475=this.addDaysSkipping(currentDate, id_0473); let id_0476="th"; const id_0477=id_0474["getDate"](); [1, 21, 31].includes(id_0477)?id_0476="st":[2, 22]["includes"](id_0477)?id_0476="nd":[3, 23]["includes"](id_0477)&&(id_0476="rd"); let id_0478="th"; const id_0479=id_0475.getDate(); [1, 21, 31]["includes"](id_0479)?id_0478="st":[2, 22].includes(id_0479)?id_0478="nd":[3, 23]["includes"](id_0479)&&(id_0478="rd"); let id_0480, id_0481; if(this["dateFormat"]==="day_dd_mm")id_0480=this["days"][id_0474["getDay"]()]+", "+id_0474["getDate"]()+". "+this["months"][id_0474["getMonth"]()], id_0481=this["days"][id_0475["getDay"]()]+", "+id_0475["getDate"]()+". "+this.months[id_0475["getMonth"]()]; else if(this.dateFormat==="mm_dd")id_0480=this["months"][id_0474["getMonth"]()]+" "+id_0474.getDate()+id_0476, id_0481=this["months"][id_0475.getMonth()]+" "+id_0475["getDate"]()+id_0478; else if(this["dateFormat"]==="dd_mm")id_0480=id_0474["getDate"]()+". "+this["months"][id_0474["getMonth"]()], id_0481=id_0475["getDate"]()+". "+this["months"][id_0475["getMonth"]()]; else if(this["dateFormat"]==="dd_mm_no_dot")id_0480=id_0474["getDate"]()+" "+this["months"][id_0474["getMonth"]()], id_0481=id_0475["getDate"]()+" "+this["months"][id_0475["getMonth"]()]; else if(this.dateFormat==="day_dd_mm_numeric")
      {
        const id_0482=String(id_0474.getDate())["padStart"](2, "0"), id_0483=String(id_0474["getMonth"]()+1)["padStart"](2, "0"); id_0480=this["days"][id_0474.getDay()]+", "+id_0482+". "+id_0483+"."; const id_0484=String(id_0475["getDate"]())["padStart"](2, "0"), id_0485=String(id_0475["getMonth"]()+1)["padStart"](2, "0"); id_0481=this["days"][id_0475.getDay()]+", "+id_0484+". "+id_0485+"."
      }
      else if(this["dateFormat"]==="dd_mm_numeric")
      {
        const id_0486=String(id_0474["getDate"]()).padStart(2, "0"), id_0487=String(id_0474["getMonth"]()+1)["padStart"](2, "0"); id_0480=id_0486+". "+id_0487+"."; const id_0488=String(id_0475["getDate"]()).padStart(2, "0"), id_0489=String(id_0475["getMonth"]()+1)["padStart"](2, "0"); id_0481=id_0488+". "+id_0489+"."
      }
      else id_0480=this["days"][id_0474.getDay()]+", "+this["months"][id_0474["getMonth"]()]+" "+id_0474.getDate()+id_0476, id_0481=this["days"][id_0475["getDay"]()]+", "+this["months"][id_0475.getMonth()]+" "+id_0475["getDate"]()+id_0478; const id_0490=id_0471.replace("[start_date]", id_0480), id_0491=id_0490["replace"]("[end_date]", id_0481); id_0470.innerHTML=id_0491
    })
  }
  rearrangeDays(id_0492)
  {
    return id_0492.unshift(id_0492[6]),
    id_0492["length"]=7,
    id_0492
  }
}
customElements.define("dynamic-dates", DynamicDates);
function refreshSectionOwnedElements()
{
  document.querySelectorAll("section-group").forEach(sectionGroup=>sectionGroup.transferSections()),
  document.querySelectorAll("dynamic-dates").forEach(dynamicDates=>dynamicDates.insertDates())
}
document.addEventListener("shopify:section:load", refreshSectionOwnedElements);
function refreshFixedPatternLayers()
{
  document.querySelectorAll('[class*="section-pattern-"], [class*="-background-pattern"], [class*="-shape-pattern"]').forEach(patternSection=>{
    const beforeMode=getComputedStyle(patternSection, "::before").getPropertyValue("--pattern-fixed-coordinate-mode").trim(),
    afterMode=getComputedStyle(patternSection, "::after").getPropertyValue("--pattern-fixed-coordinate-mode").trim(),
    usesFixedClip=beforeMode==="viewport-clip"||afterMode==="viewport-clip";
    let clip=Array.from(patternSection.children).find(child=>child.classList.contains("pattern-fixed-viewport-clip"));
    if(!usesFixedClip)
    {
      clip?.remove(),
      patternSection.classList.remove("pattern-fixed-clip-ready");
      return
    }
    if(!clip)
    {
      const layer=document.createElement("div");
      clip=document.createElement("div"),
      clip.className="pattern-fixed-viewport-clip",
      clip.setAttribute("aria-hidden", "true"),
      layer.className="pattern-fixed-viewport-layer",
      layer.textContent="\u00a0",
      clip.append(layer),
      patternSection.prepend(clip)
    }
    const layer=clip.firstElementChild;
    layer&&!layer.textContent&&(layer.textContent="\u00a0");
    const clipStyle=getComputedStyle(clip), layerStyle=layer&&getComputedStyle(layer),
    clipIsActive=(clipStyle.clipPath!=="none"||clipStyle.webkitClipPath!=="none")&&layerStyle?.display!=="none"&&layerStyle?.position==="fixed";
    patternSection.classList.toggle("pattern-fixed-clip-ready", clipIsActive),
    clipIsActive||clip.remove()
  })
}
refreshFixedPatternLayers(),
document.addEventListener("shopify:section:load", refreshFixedPatternLayers),
document.addEventListener("shopify:section:reorder", refreshFixedPatternLayers);
(function () {
  var loaderScript = document.currentScript;
  var productSrc = loaderScript && loaderScript.dataset.mainProductSrc;
  if (!productSrc || !document.querySelector('quick-add-modal,product-form,product-info,sticky-atc,bundle-deals,quantity-breaks,exa-subscription-widget,quantity-gifts,quantity-break-upsells,product-info-upsell,cart-drawer-upsell,cart-drawer-gift,main-bundle-offer,custom-product-field,variant-selects,secondary-variant-select,secondary-variant-select-separate,card-variant-selects')) return;
  var productScript = document.createElement('script');
  productScript.src = productSrc;
  productScript.async = false;
  productScript.dataset.defer = 'true';
  document.head.appendChild(productScript);
})();
